// src/kb.js
//
// ═══════════════════════════════════════════════════════════════
// LENDER LIBRARY — browser-side library (no React in here)
// ═══════════════════════════════════════════════════════════════
// The contract is docs/lender-library-design.md ("Client library" + "Shared
// constants"). The UI (src/views/LenderLibrary.jsx) only talks to this file.
//
//   kb_documents / kb_pages   one row per document, one row per page of text
//   storage 'lender-library'  the original files, at `${docId}/${safeName}`
//   /api/anthropic-proxy      action "kb-ocr" (scan → text) and "kb-ask"
//                             (the chatbot, streamed back as NDJSON)
//
// Ingest happens HERE, in the browser: pdf.js pulls the text layer out of
// each PDF page; a page with (almost) no text layer — or only a little text
// over a big picture — is a scan, so it is rendered to a JPEG and
// transcribed by Claude through kb-ocr. That keeps the big files off the
// 4.5 MB / 60 s serverless function — only one page image at a time ever
// goes through it. Word / Excel files (.docx / .xlsx) are unzipped and read
// here too (unzipText / docxText / xlsxSheets) — never indexed as raw bytes.
//
// Pages are saved as they're produced, so nothing paid for is thrown away:
// a scanned page that still can't be transcribed after retries is skipped
// (the document goes 'ready' with a warning naming it), and retryIngest()
// resumes any unfinished document — only the missing pages run again.
//
// Every exported function throws an Error whose message is written for Reece
// to read on screen ("…failed: your admin session has expired…"), never a raw
// PostgREST code. err.code / err.status ride along for the UI when useful.
//
// CSP NOTES (vercel.json): no fetch() of data: URLs anywhere — base64 comes
// from FileReader on a Blob, and the canvas fallback uses lib.js
// dataUrlToBlob. pdf.js is bundled (lazy chunk) with its worker served from
// /pdf.worker.min.js and isEvalSupported:false (script-src 'self', no eval).

import { supabase, isSupabaseReady } from "./supabase";
import { dataUrlToBlob } from "./lib";

// ── Shared constants (imported by the UI) ─────────────────────
export const KB_LENDERS = ["Finance of America","Mutual of Omaha","SmartFi","Longbridge","HUD / FHA","Other"];
export const KB_DOC_TYPES = ["Guideline","Program update / bulletin","Rate sheet","Matrix / product sheet","Training / FAQ","Email / AE note","Other"];
export const KB_BUCKET = "lender-library";
// Convenience for <input type="file" accept=…> — the types ingestFile takes.
export const KB_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.docx,.xlsx";

const PROXY_URL = "/api/anthropic-proxy";
const PDF_WORKER_SRC = "/pdf.worker.min.js";
const SQL_FILE = "supabase/2026-10-01-lender-library.sql";

const MIN_TEXT_CHARS = 40;        // fewer non-space chars than this on a PDF page → treat as a scan
const SPARSE_TEXT_CHARS = 1500;   // under this many, also check whether pictures carry the page's content…
const IMAGE_COVER_MIN = 0.25;     // …and transcribe it when images cover at least this share of the page
const OCR_RENDER_WIDTH = 1600;    // px wide when rendering a scanned PDF page for transcription
const OCR_JPEG_QUALITY = 0.85;
const IMAGE_MAX_EDGE = 2000;      // uploaded photos/screenshots are shrunk to this before transcription
const MAX_OCR_BASE64 = 3500000;   // keep the kb-ocr body well under Vercel's ~4.5 MB request cap
const OCR_CONCURRENCY = 3;        // scanned pages transcribed in parallel
const OCR_RETRY_DELAYS = [2000, 6000];
const OCR_TIMEOUT_MS = 90000;     // the function itself is capped at 60 s
const OCR_FAIL_STREAK = 4;        // this many scanned pages failing in a row → stop (something bigger is wrong)
const HEARTBEAT_MS = 60000;       // a running ingest touches its row's updated_at this often (see STALE note in the UI)
const PDF_LINK_SECONDS = 3600;    // the page viewer streams PDFs from a signed link this long-lived…
const PDF_LINK_MAX_AGE = 50 * 60000; // …and re-opens a cached PDF before that link runs out
const PDF_RANGE_CHUNK = 262144;   // bytes per range request when streaming a PDF for the viewer
const STREAM_IDLE_MS = 90000;     // kb-ask: give up if no bytes arrive for this long
const TEXT_PAGE_CHARS = 3000;     // pasted notes / .txt / .md / .csv → "pages" of about this size
const PAGE_BATCH = 50;            // kb_pages rows per insert
const LIST_PAGE = 1000;           // PostgREST's default max rows per request
const PDF_CACHE_MAX = 3;          // loaded PDFs kept in memory for the page viewer
const MAX_RENDER_PIXELS = 12000000;
const MAX_QUESTION = 4000;
const MAX_HISTORY = 12;
const MAX_HISTORY_TEXT = 6000;

// ═══════════════════════════════════════════════════════════════
// Small shared helpers
// ═══════════════════════════════════════════════════════════════

function requireDb() {
  if (!isSupabaseReady()) {
    throw new Error("The Lender Library needs the Supabase database, and it isn't connected here " +
      "(REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY are missing). Reload once they're set.");
  }
  return supabase;
}

function nowIso() { return new Date().toISOString(); }
function sleep(ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); }
function errText(e) { return (e && e.message) || String(e || "unknown error"); }

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1048576) return (b / 1048576).toFixed(1) + " MB";
  if (b >= 1024) return Math.round(b / 1024) + " KB";
  return b + " bytes";
}

function plural(n, word) { return n + " " + word + (n === 1 ? "" : "s"); }

function abortError() {
  const e = new Error("Stopped.");
  e.name = "AbortError";
  e.aborted = true;
  return e;
}

function isNetworkish(msg) {
  return /failed to fetch|networkerror|network request failed|load failed|timed out|timeout|aborted|abort/i.test(msg || "");
}

// Database (PostgREST) error → a sentence Reece can act on.
function dbError(what, error) {
  const code = String((error && error.code) || "");
  const msg = errText(error);
  let text;
  if (code === "42P01" || code === "PGRST205" || code === "PGRST202" || code === "42883" || code === "42703" ||
      /does not exist|schema cache|could not find the function/i.test(msg)) {
    text = what + ": the Lender Library isn't set up in the database yet. Run " + SQL_FILE +
      " in the Supabase SQL Editor, then reload the CRM.";
  } else if (code === "42501" || /row-level security|permission denied/i.test(msg)) {
    text = what + ": permission denied. Make sure you're signed in as the admin account.";
  } else if (code === "PGRST116") {
    text = what + ": that document isn't in the library any more (it may have been deleted). Reload the library.";
  } else if (code === "23503") {
    text = what + ": the document record disappeared while this was running (was it deleted?).";
  } else if (isNetworkish(msg)) {
    text = what + ": couldn't reach the database (network problem or timeout). Check your connection and try again.";
  } else {
    text = what + ": " + msg;
  }
  const e = new Error(text);
  e.code = code;
  e.transient = !code && isNetworkish(msg);
  return e;
}

// Storage error → a sentence Reece can act on. The upload-size case matters:
// the project-wide upload limit in Supabase (Storage → Settings) caps every
// bucket, whatever the bucket itself allows.
function storageError(what, error) {
  const msg = errText(error);
  const status = Number((error && (error.status || error.statusCode)) || 0);
  let text;
  if (/bucket not found/i.test(msg)) {
    text = what + ": the \"" + KB_BUCKET + "\" storage bucket doesn't exist yet. Run " + SQL_FILE +
      " in the Supabase SQL Editor (it creates the bucket), then try again.";
  } else if (status === 413 || /too large|maximum allowed size|exceeded the maximum|entity too large/i.test(msg)) {
    text = what + ": the file is bigger than Supabase's upload limit. Raise it in Supabase → Storage → Settings " +
      "(global file size limit) and on the " + KB_BUCKET + " bucket, then retry.";
  } else if (/mime type|not supported/i.test(msg)) {
    text = what + ": the " + KB_BUCKET + " bucket is refusing this file type (" + msg + "). Clear the bucket's " +
      "\"allowed MIME types\" in Supabase → Storage, then retry.";
  } else if (status === 401 || status === 403 || /row-level security|unauthorized|not authorized|permission/i.test(msg)) {
    text = what + ": permission denied by Supabase Storage. Sign in as the admin, and make sure " + SQL_FILE +
      " has been run (it adds the kb_storage_* policies).";
  } else if (/not found/i.test(msg)) {
    text = what + ": the stored file is missing from Supabase Storage.";
  } else if (isNetworkish(msg)) {
    text = what + ": the transfer didn't finish (a network problem, or a very large file on a slow connection — " +
      "library files get 30 minutes). Try again on a steadier connection.";
  } else {
    text = what + ": " + msg;
  }
  const e = new Error(text);
  e.status = status;
  e.transient = isNetworkish(msg);
  return e;
}

// Non-200 from /api/anthropic-proxy → Error. Handles the proxy's own
// {error, detail|message} JSON, the kb actions' {code, message}, and
// Vercel's plain-text platform errors (FUNCTION_INVOCATION_TIMEOUT, …).
async function httpError(resp, what) {
  let raw = "";
  try { raw = await resp.text(); } catch (_e) { raw = ""; }
  let body = null;
  try { body = raw ? JSON.parse(raw) : null; } catch (_e) { body = null; }
  const vercelCode = (/FUNCTION_[A-Z_]+/.exec(raw) || [""])[0];
  const code = String((body && (body.code || body.error)) || vercelCode || "");
  let serverMsg = "";
  if (body && typeof body.message === "string") serverMsg = body.message;
  else if (body && typeof body.detail === "string") serverMsg = body.detail;
  const s = resp.status;
  let msg;
  if (s === 401) msg = "your admin session has expired. Sign out, sign back in, and try again.";
  else if (s === 403) msg = "this login isn't allowed to use the Lender Library (admin only).";
  else if (s === 404) msg = "the server endpoint " + PROXY_URL + " wasn't found. The AI features run on the deployed site (or `vercel dev`), not plain `npm start`.";
  else if (s === 413 || code === "FUNCTION_PAYLOAD_TOO_LARGE") msg = "the request was too large for the server (Vercel caps a request at about 4.5 MB).";
  else if (code === "invalid_model" || code === "messages_required") msg = "the deployed server doesn't know about the Lender Library yet. Deploy the updated api/anthropic-proxy.js and try again.";
  else if (code === "refusal" || s === 422) msg = serverMsg || "Claude declined to process this.";
  else if (s === 429) msg = "the AI service is rate-limiting requests right now. Wait a minute and try again.";
  else if (code === "anthropic_key_not_configured") msg = "the Anthropic API key (ANTHROPIC_KEY) isn't set on the server. Add it in Vercel → Settings → Environment Variables and redeploy.";
  else if (s === 504 || code === "FUNCTION_INVOCATION_TIMEOUT") msg = "the server ran out of time (60-second limit). Try again; if it keeps happening, the request is too big.";
  else if (s === 400 || code === "bad_request") msg = serverMsg || ("the server rejected the request" + (code ? " (" + code + ")" : "") + ".");
  else if (s >= 500) msg = serverMsg ? "the server had a problem: " + serverMsg : "the server had a problem (HTTP " + s + (code ? ", " + code : "") + "). Try again in a minute.";
  else msg = serverMsg || ("unexpected response from the server (HTTP " + s + ").");
  const err = new Error(what + " failed: " + msg);
  err.status = s;
  err.code = code;
  err.transient = s === 429 || s === 502 || s === 503 || s === 504 || s === 529 ||
    (s === 500 && code !== "anthropic_key_not_configured");
  return err;
}

// An AbortController that follows an outer signal and (optionally) fires on
// its own after `timeoutMs` without a bump() — an idle timeout, so a stalled
// connection can't hang an ingest or a chat forever.
function linkedController(outer, timeoutMs) {
  const ctl = new AbortController();
  const state = { timedOut: false };
  let timer = null;
  function onAbort() { ctl.abort(); }
  if (outer) {
    if (outer.aborted) ctl.abort();
    else outer.addEventListener("abort", onAbort, { once: true });
  }
  function bump() {
    if (!timeoutMs) return;
    clearTimeout(timer);
    timer = setTimeout(function () { state.timedOut = true; ctl.abort(); }, timeoutMs);
  }
  function done() {
    clearTimeout(timer);
    if (outer) outer.removeEventListener("abort", onAbort);
  }
  bump();
  return { signal: ctl.signal, state, bump, done };
}

// A fetch()/read() that threw → Error. Distinguishes Stop, idle timeout, and
// a plain network failure.
function fetchFailure(link, outer, what, midStream) {
  if (link && link.state.timedOut) {
    const t = new Error(what + " failed: the server stopped responding. Try again.");
    t.transient = true;
    return t;
  }
  if (outer && outer.aborted) return abortError();
  const err = new Error(what + " failed: " + (midStream
    ? "the connection dropped partway through. Try again."
    : "couldn't reach the server. Check your internet connection and try again."));
  err.status = 0;
  err.transient = true;
  return err;
}

function emit(fn, evt) {
  if (typeof fn !== "function") return;
  try { fn(evt); } catch (e) { console.error("[kb] event handler threw", e); }
}

// ── Document ids / names / kinds ──────────────────────────────

// 'KB-' + base36 timestamp + 3 random base36 chars (contract format).
export function newKbDocId() {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const bytes = new Uint8Array(3);
  const c = typeof window !== "undefined" && window.crypto ? window.crypto : null;
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  let rnd = "";
  for (let i = 0; i < bytes.length; i++) rnd += alphabet[bytes[i] % 36];
  return "KB-" + Date.now().toString(36).toUpperCase() + rnd;
}

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXT_KIND = { pdf: "pdf", png: "image", jpg: "image", jpeg: "image", webp: "image", txt: "text", md: "text", markdown: "text", csv: "text", docx: "docx", xlsx: "xlsx" };
const EXT_MIME = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", txt: "text/plain", md: "text/markdown", markdown: "text/markdown", csv: "text/csv", docx: DOCX_MIME, xlsx: XLSX_MIME };

function extOf(name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ""));
  return m ? m[1].toLowerCase() : "";
}

function baseMime(mime) { return String(mime || "").toLowerCase().split(";")[0].trim(); }

function kindFromMime(mime) {
  const t = baseMime(mime);
  if (t === "application/pdf") return "pdf";
  if (t === "image/png" || t === "image/jpeg" || t === "image/jpg" || t === "image/webp") return "image";
  if (t === "text/plain" || t === "text/markdown" || t === "text/x-markdown" || t === "text/csv") return "text";
  if (t === DOCX_MIME) return "docx";
  if (t === XLSX_MIME) return "xlsx";
  return null;
}

// The extension wins over the browser's guess (Windows reports .csv as
// application/vnd.ms-excel and .md as nothing at all).
function fileKind(file) {
  return EXT_KIND[extOf(file && file.name)] || kindFromMime(file && file.type);
}

function mimeFor(file, kind) {
  const byExt = EXT_MIME[extOf(file && file.name)];
  if (byExt) return byExt;
  const t = baseMime(file && file.type);
  if (t === "image/jpg") return "image/jpeg";
  if (t) return t;
  if (kind === "pdf") return "application/pdf";
  return kind === "image" ? "image/jpeg" : "text/plain";
}

// 'pdf' | 'image' | 'text' | null for a kb_documents row (pasted notes → 'text').
// This is how the page VIEWER treats it; officeKind() is how it's read.
export function docKind(doc) {
  if (!doc) return null;
  // Excel / Word originals have no page image — their stored page text IS
  // the page, so view them like pasted notes.
  if (officeKind(doc)) return "text";
  return kindFromMime(doc.mime) || EXT_KIND[extOf(doc.file_name || doc.file_path)] || (doc.file_path ? null : "text");
}

// 'docx' | 'xlsx' (readable here — see extractOfficePages), 'legacy' (the
// old binary .doc / .xls, which isn't), or null, for a document row or a File.
export function officeKind(doc) {
  if (!doc) return null;
  const ext = extOf(doc.file_name || doc.file_path || doc.name);
  if (ext === "docx") return "docx";
  if (ext === "xlsx" || ext === "xlsm") return "xlsx";
  if (ext === "doc" || ext === "xls") return "legacy";
  if (EXT_KIND[ext]) return null;   // e.g. a .csv that Windows labels application/vnd.ms-excel
  const m = baseMime(doc.mime || doc.type);
  if (m === DOCX_MIME) return "docx";
  if (m === XLSX_MIME) return "xlsx";
  if (/officedocument\.(spreadsheetml|wordprocessingml)|ms-excel|msword/.test(m)) return "legacy";
  return null;
}

// Storage object keys must stay plain: ASCII letters, digits, dot, dash, underscore.
function safeFileName(name) {
  let base = String(name || "").split(/[\\/]/).pop() || "";
  if (base.normalize) base = base.normalize("NFKD").replace(/[̀-ͯ]/g, "");
  base = base.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/_+/g, "_").replace(/^[._]+/, "");
  if (!base) base = "file";
  if (base.length > 120) {
    const m = /(\.[A-Za-z0-9]{1,8})$/.exec(base);
    const ext = m ? m[1] : "";
    base = base.slice(0, 120 - ext.length) + ext;
  }
  return base;
}

function titleFromFileName(name) {
  return String(name || "").split(/[\\/]/).pop().replace(/\.[A-Za-z0-9]{1,8}$/, "").trim();
}

// ── Metadata normalization ────────────────────────────────────

function canonical(list, value, fallback, label) {
  if (value == null || String(value).trim() === "") return fallback;
  const v = String(value).trim();
  const hit = list.find(function (x) { return x.toLowerCase() === v.toLowerCase(); });
  if (!hit) throw new Error("\"" + v + "\" isn't a valid " + label + ". Choose one of: " + list.join(", ") + ".");
  return hit;
}

function normalizePrograms(v) {
  const raw = Array.isArray(v) ? v : String(v == null ? "" : v).split(",");
  const seen = new Set();
  const out = [];
  raw.forEach(function (p) {
    const t = String(p == null ? "" : p).replace(/\s+/g, " ").trim().slice(0, 80);
    if (!t || seen.has(t.toLowerCase())) return;
    seen.add(t.toLowerCase());
    out.push(t);
  });
  return out.slice(0, 30);
}

function pad2(n) { return (n < 10 ? "0" : "") + n; }

// '' / null → null. Accepts YYYY-MM-DD (the date input's value), M/D/YY(YY), or a Date.
function normalizeDate(v) {
  if (v == null || v === "") return null;
  let y, mo, d;
  if (v instanceof Date) {
    if (isNaN(v.getTime())) throw new Error("The effective date isn't a valid date.");
    y = v.getFullYear(); mo = v.getMonth() + 1; d = v.getDate();
  } else {
    const s = String(v).trim();
    if (!s) return null;
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
    if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
    else if ((m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(s))) {
      mo = +m[1]; d = +m[2]; y = +m[3];
      if (y < 100) y += 2000;
    } else {
      throw new Error("\"" + s + "\" isn't a date. Use YYYY-MM-DD, e.g. 2026-09-29.");
    }
    const check = new Date(Date.UTC(y, mo - 1, d));
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
      throw new Error("\"" + s + "\" isn't a real calendar date.");
    }
  }
  return y + "-" + pad2(mo) + "-" + pad2(d);
}

function normalizeMeta(meta, fallbackTitle) {
  const m = meta || {};
  const title = (String(m.title == null ? "" : m.title).trim() || String(fallbackTitle || "").trim()).slice(0, 300);
  if (!title) throw new Error("Give the document a title.");
  return {
    title,
    lender: canonical(KB_LENDERS, m.lender, "Other", "lender"),
    doc_type: canonical(KB_DOC_TYPES, m.doc_type, "Guideline", "document type"),
    programs: normalizePrograms(m.programs),
    effective_date: normalizeDate(m.effective_date),
    notes: String(m.notes == null ? "" : m.notes).trim(),
  };
}

// ═══════════════════════════════════════════════════════════════
// Text cleanup + splitting (pure — no browser APIs)
// ═══════════════════════════════════════════════════════════════

const LIGATURES = ["ff", "fi", "fl", "ffi", "ffl", "st", "st"]; // U+FB00..U+FB06

// Makes text safe for Postgres and friendly to full-text search: drops NUL and
// other control chars (Postgres rejects \u0000 outright), lone UTF-16
// surrogates (rejected by the JSON → text path), soft hyphens and zero-width
// chars (they split words in the index); unfolds fi/fl ligatures; turns
// non-breaking spaces into spaces and CRLF/form-feeds into newlines.
function sanitizeText(input) {
  const t = String(input == null ? "" : input);
  let out = "";
  for (let i = 0; i < t.length; i++) {
    const c = t.charCodeAt(i);
    if (c >= 0xD800 && c <= 0xDBFF) {
      const n = t.charCodeAt(i + 1);
      if (n >= 0xDC00 && n <= 0xDFFF) { out += t[i] + t[i + 1]; i++; }
      continue;
    }
    if (c >= 0xDC00 && c <= 0xDFFF) continue;
    if (c === 9 || c === 10) { out += t[i]; continue; }
    if (c === 13) { if (t.charCodeAt(i + 1) !== 10) out += "\n"; continue; }
    if (c === 12) { out += "\n\n"; continue; }
    if (c < 32 || c === 127) continue;
    if (c === 0xA0 || c === 0x2007 || c === 0x202F) { out += " "; continue; }
    if (c === 0xAD || (c >= 0x200B && c <= 0x200D) || c === 0xFEFF) continue;
    if (c >= 0xFB00 && c <= 0xFB06) { out += LIGATURES[c - 0xFB00]; continue; }
    out += t[i];
  }
  return out;
}

function nonSpaceLen(text) { return String(text || "").replace(/[\s|]+/g, "").length; }

// One over-long line → pieces of at most `size` chars, broken at spaces when
// possible (a CSV row or a run-on paste with no line breaks).
function splitLongLine(line, size, max) {
  if (line.length <= max) return [line];
  const out = [];
  let cur = "";
  const words = line.split(/\s+/);
  for (let i = 0; i < words.length; i++) {
    let w = words[i];
    while (w.length > size) {
      if (cur) { out.push(cur); cur = ""; }
      out.push(w.slice(0, size));
      w = w.slice(size);
    }
    if (!w) continue;
    if (cur && cur.length + 1 + w.length > size) { out.push(cur); cur = w; }
    else cur = cur ? cur + " " + w : w;
  }
  if (cur) out.push(cur);
  return out;
}

// Text → array of "page" strings of about `target` chars, broken on paragraph
// boundaries (then lines, then words, never mid-word unless a "word" is
// itself enormous). opts.repeatHeader: repeat the first line on every page
// (CSV — so each page still says what its columns are).
export function splitTextPages(text, target, opts) {
  const size = Math.max(200, Number(target) || TEXT_PAGE_CHARS);
  const max = Math.round(size * 1.4);
  const clean = sanitizeText(text).replace(/[ \t]+\n/g, "\n").trim();
  if (!clean) return [];
  const header = opts && opts.repeatHeader ? clean.split("\n", 1)[0].trim() : "";

  // 1) Units no longer than `max`, each with the separator that joins it to
  //    the unit before it when both land on the same page.
  const units = [];
  const paras = clean.split(/\n[ \t]*\n+/);
  for (let p = 0; p < paras.length; p++) {
    const para = paras[p].trim();
    if (!para) continue;
    if (para.length <= max) { units.push({ text: para, sep: "\n\n" }); continue; }
    let sep = "\n\n";
    const lines = para.split("\n");
    for (let l = 0; l < lines.length; l++) {
      const line = lines[l].replace(/\s+$/, "");
      if (!line.trim()) continue;
      const pieces = splitLongLine(line, size, max);
      for (let k = 0; k < pieces.length; k++) {
        units.push({ text: pieces[k], sep: k === 0 ? sep : " " });
      }
      sep = "\n";
    }
  }

  // 2) Pack units greedily into pages of up to `size`. pageSeps remembers how
  //    each page's first unit joined the text before it (for step 3).
  const pages = [];
  const pageSeps = [];
  let cur = "";
  let curSep = "\n\n";
  for (let u = 0; u < units.length; u++) {
    const unit = units[u];
    if (!cur) { cur = unit.text; curSep = unit.sep; }
    else if (cur.length + unit.sep.length + unit.text.length <= size) cur += unit.sep + unit.text;
    else { pages.push(cur); pageSeps.push(curSep); cur = unit.text; curSep = unit.sep; }
  }
  if (cur) { pages.push(cur); pageSeps.push(curSep); }

  // 3) Fold a runt last page back into its neighbor when that stays reasonable.
  const n = pages.length;
  if (n > 1 && pages[n - 1].length < size * 0.2 && pages[n - 2].length + pages[n - 1].length + 2 <= max) {
    const last = pages.pop();
    pages[pages.length - 1] += pageSeps[n - 1] + last;
  }

  if (header) {
    for (let i = 1; i < pages.length; i++) {
      if (!pages[i].startsWith(header)) pages[i] = header + "\n" + pages[i];
    }
  }
  return pages;
}

// ── NDJSON ────────────────────────────────────────────────────
// Feed it decoded text chunks in any sizes; onObject(obj, rawLine) fires once
// per complete line. A line split across chunks is carried over; end()
// flushes a last line that had no trailing newline. Unparseable lines are
// reported as onObject(null, rawLine) so the caller can surface them.
export function createNdjsonParser(onObject) {
  let buf = "";
  function handle(line) {
    const s = line.trim();
    if (!s) return;
    let obj = null;
    try { obj = JSON.parse(s); } catch (_e) { obj = null; }
    onObject(obj && typeof obj === "object" ? obj : null, s);
  }
  return {
    push(chunk) {
      if (!chunk) return;
      buf += chunk;
      let start = 0;
      let nl;
      while ((nl = buf.indexOf("\n", start)) !== -1) {
        handle(buf.slice(start, nl));
        start = nl + 1;
      }
      if (start) buf = buf.slice(start);
    },
    end() {
      const rest = buf;
      buf = "";
      if (rest) handle(rest);
    },
  };
}

// ── PDF text layer → reading-order text ───────────────────────
// pdf.js hands back positioned text runs. Sort by baseline (top of page
// first) then x; runs that overlap vertically by at least half the smaller
// run's height are one line (so superscripts and baseline jitter stay put);
// each line is re-sorted left→right and joined with a space where there is a
// visible gap, or " | " where the gap is a column gutter (tables, two-column
// layouts) so cells don't run together. Lines are joined with "\n".
function joinLine(items) {
  items.sort(function (a, b) { return a.x - b.x; });
  let out = "";
  let prev = null;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    let s = it.s;
    if (!s.trim()) {                       // pdf.js's own space runs
      if (out && !/\s$/.test(out)) out += " ";
      continue;
    }
    if (prev) {
      const em = Math.max(Math.min(prev.h, it.h), 1);
      if (s === prev.s && Math.abs(it.x - prev.x) < em * 0.5) continue;   // fake-bold overprint
      const gap = it.x - (prev.x + prev.w);
      if (gap > em * 2) {
        out = out.replace(/\s+$/, "") + " | ";
        s = s.replace(/^\s+/, "");
      } else if (gap > em * 0.15 && !/\s$/.test(out) && !/^\s/.test(s)) {
        out += " ";
      }
    }
    out += s;
    prev = it;
  }
  return out.replace(/[ \t]{2,}/g, " ").trim();
}

export function pdfItemsToText(items) {
  const runs = [];
  const list = items || [];
  for (let i = 0; i < list.length; i++) {
    const it = list[i];
    if (!it || typeof it.str !== "string" || it.str === "") continue;
    const tr = it.transform || [1, 0, 0, 1, 0, 0];
    const h = Math.abs(it.height) || Math.hypot(tr[2], tr[3]) || Math.hypot(tr[0], tr[1]) || 10;
    const w = Math.abs(it.width) || it.str.length * h * 0.5;
    runs.push({ s: it.str, x: Number(tr[4]) || 0, y: Number(tr[5]) || 0, w, h });
  }
  if (!runs.length) return "";
  runs.sort(function (a, b) { return (b.y - a.y) || (a.x - b.x); });

  const lines = [];
  let cur = null;
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i];
    const bot = r.y;
    const top = r.y + r.h;
    if (cur) {
      const overlap = Math.min(cur.top, top) - Math.max(cur.bot, bot);
      if (overlap >= Math.min(cur.top - cur.bot, r.h) * 0.5 || Math.abs(cur.y - r.y) <= 1) {
        cur.items.push(r);
        if (top > cur.top) cur.top = top;
        if (bot < cur.bot) cur.bot = bot;
        continue;
      }
    }
    cur = { y: r.y, top, bot, items: [r] };
    lines.push(cur);
  }
  return sanitizeText(lines.map(function (l) { return joinLine(l.items); }).filter(Boolean).join("\n"));
}

// ── Word / Excel (.docx / .xlsx) → text ───────────────────────
// Both are ZIP files of XML. The browser has no unzip, but it has
// DecompressionStream("deflate-raw") (Chrome/Edge 103+, Safari 16.4+,
// Firefox 113+), which is all a ZIP's deflated entries need. Sizes and
// offsets come from the central directory (local headers may leave them 0).
const ZIP_ENTRY_MAX = 80 * 1048576;   // refuse a single XML part bigger than this uncompressed

async function inflateRaw(bytes) {
  if (typeof DecompressionStream === "undefined" || typeof Response === "undefined") {
    throw new Error("This browser can't open Word or Excel files. Use a current Chrome, Edge, Safari or Firefox — or save the file as a PDF and add that.");
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// ZIP bytes → { entryName: text } for the entries want(name) accepts.
// `inflate` (bytes → Promise<bytes>) is swappable for tests.
export async function unzipText(buf, want, inflate) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const bad = function (why) {
    return new Error("This isn't a readable Word/Excel file (" + why + "). Open it, save it again as .docx / .xlsx (or as a PDF), and add that.");
  };
  let eocd = -1;
  for (let i = u8.length - 22; i >= 0 && i >= u8.length - 65557; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw bad("it isn't a ZIP package");
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  if (count === 0xFFFF || p === 0xFFFFFFFF) throw bad("ZIP64 packages aren't supported");
  const dec = new TextDecoder("utf-8");
  const out = {};
  for (let k = 0; k < count; k++) {
    if (p + 46 > u8.length || dv.getUint32(p, true) !== 0x02014b50) throw bad("its ZIP directory is damaged");
    const flags = dv.getUint16(p + 8, true);
    const method = dv.getUint16(p + 10, true);
    const csize = dv.getUint32(p + 20, true);
    const usize = dv.getUint32(p + 24, true);
    const nlen = dv.getUint16(p + 28, true);
    const skip = nlen + dv.getUint16(p + 30, true) + dv.getUint16(p + 32, true);
    const loc = dv.getUint32(p + 42, true);
    const name = dec.decode(u8.subarray(p + 46, p + 46 + nlen));
    p += 46 + skip;
    if (!want(name)) continue;
    if (flags & 1) throw bad("it's password-protected");
    if (usize > ZIP_ENTRY_MAX) throw bad("one of its parts is over " + fmtBytes(ZIP_ENTRY_MAX));
    if (loc + 30 > u8.length || dv.getUint32(loc, true) !== 0x04034b50) throw bad("a ZIP entry is damaged");
    const start = loc + 30 + dv.getUint16(loc + 26, true) + dv.getUint16(loc + 28, true);
    const data = u8.subarray(start, start + csize);
    let bytes;
    if (method === 0) bytes = data;
    else if (method === 8) bytes = await (inflate || inflateRaw)(data);
    else throw bad("compression method " + method + " isn't supported");
    out[name] = dec.decode(bytes);
  }
  return out;
}

function parseXml(text, what) {
  if (typeof DOMParser === "undefined") throw new Error("This browser can't read " + what + ".");
  const doc = new DOMParser().parseFromString(String(text || ""), "application/xml");
  if (!doc || !doc.documentElement || doc.getElementsByTagName("parsererror").length) {
    throw new Error(what + " is damaged (its XML doesn't parse). Open it, save it again, and add that copy.");
  }
  return doc;
}

function childNamed(el, ln) {
  for (let c = el && el.firstElementChild; c; c = c.nextElementSibling) if (c.localName === ln) return c;
  return null;
}

// Nearest ancestor with this local name (null at the top).
function ownerNamed(el, ln) {
  let n = el.parentNode;
  while (n && n.localName !== ln) n = n.parentNode;
  return n || null;
}

// Word containers whose contents are never body text: formatting, section
// layout, field codes, deleted/moved-away revisions — and the Fallback copy
// of every drawing (mc:AlternateContent repeats each text box for old Word).
const DOCX_SKIP = new Set(["Fallback", "pPr", "rPr", "sectPr", "tblPr", "trPr", "tcPr", "tblGrid", "instrText", "delInstrText", "delText", "del", "moveFrom"]);

function docxBlocks(node, out) {
  for (let el = node.firstElementChild; el; el = el.nextElementSibling) {
    const ln = el.localName;
    if (DOCX_SKIP.has(ln)) continue;
    if (ln === "p") docxParagraph(el, out);
    else if (ln === "tbl") docxTable(el, out);
    else docxBlocks(el, out);
  }
}

function docxRuns(node, parts, nested) {
  for (let el = node.firstElementChild; el; el = el.nextElementSibling) {
    const ln = el.localName;
    if (DOCX_SKIP.has(ln)) continue;
    if (ln === "t") parts.push(el.textContent || "");
    else if (ln === "tab" || ln === "ptab") parts.push("\t");
    else if (ln === "br" || ln === "cr") parts.push("\n");
    else if (ln === "noBreakHyphen") parts.push("-");
    else if (ln === "txbxContent") docxBlocks(el, nested);   // a text box: its own lines, after this paragraph
    else if (ln === "p") docxParagraph(el, nested);
    else if (ln === "tbl") docxTable(el, nested);
    else docxRuns(el, parts, nested);
  }
}

function docxParagraph(p, out) {
  const parts = [];
  const nested = [];
  docxRuns(p, parts, nested);
  let text = parts.join("");
  const pPr = childNamed(p, "pPr");
  if (text.trim() && pPr && childNamed(pPr, "numPr")) text = "- " + text.trim();   // a list item
  out.push(text);
  for (let i = 0; i < nested.length; i++) out.push(nested[i]);
}

function docxTable(tbl, out) {
  const rows = Array.prototype.filter.call(tbl.getElementsByTagNameNS("*", "tr"), function (tr) { return ownerNamed(tr, "tbl") === tbl; });
  rows.forEach(function (tr) {
    const cells = Array.prototype.filter.call(tr.getElementsByTagNameNS("*", "tc"), function (tc) { return ownerNamed(tc, "tr") === tr; })
      .map(function (tc) { const lines = []; docxBlocks(tc, lines); return lines.join(" ").replace(/\s+/g, " ").trim(); });
    if (cells.some(Boolean)) out.push(cells.join(" | "));
  });
}

// word/document.xml → plain text: one line per paragraph (list items as
// "- …"), table rows as "cell | cell", each text box's lines after the
// paragraph it's anchored in. Only real text runs (w:t) are read, so drawing
// layout values (wp:posOffset, wp:align…) never leak in, and runs are joined
// with nothing between them — Word splits "$2,800,000" into several runs
// wherever the formatting changes mid-number.
export function docxText(xml) {
  const doc = parseXml(xml, "This Word file");
  const out = [];
  docxBlocks(doc.documentElement, out);
  return out.join("\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

// Shared-string / inline-string text: every t run, minus phonetic guides.
function xlsxStringOf(el) {
  let s = "";
  const ts = el.getElementsByTagNameNS("*", "t");
  for (let i = 0; i < ts.length; i++) if (!ownerNamed(ts[i], "rPh")) s += ts[i].textContent || "";
  return s;
}

const XLSX_REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

// A workbook's XML parts (from unzipText) → [{name, text}], one per sheet in
// workbook order. Each row is a line of cells, "A6=Enter New Max Claim |
// B6=250000", and a formula follows its value as {=B6*B7} — the shape the
// bulk importer used, so re-reading a stored spreadsheet reads the same.
// Values are what Excel last saved (raw: 0.02, not 2%).
export function xlsxSheets(files) {
  const wb = parseXml(files["xl/workbook.xml"], "This Excel file");
  const targets = {};
  if (files["xl/_rels/workbook.xml.rels"]) {
    const rels = parseXml(files["xl/_rels/workbook.xml.rels"], "This Excel file");
    Array.prototype.forEach.call(rels.getElementsByTagNameNS("*", "Relationship"), function (r) {
      const t = String(r.getAttribute("Target") || "");
      targets[r.getAttribute("Id")] = t.charAt(0) === "/" ? t.slice(1) : "xl/" + t.replace(/^\.\//, "");
    });
  }
  const shared = [];
  if (files["xl/sharedStrings.xml"]) {
    const ss = parseXml(files["xl/sharedStrings.xml"], "This Excel file");
    Array.prototype.forEach.call(ss.getElementsByTagNameNS("*", "si"), function (si) { shared.push(xlsxStringOf(si)); });
  }
  const out = [];
  Array.prototype.forEach.call(wb.getElementsByTagNameNS("*", "sheet"), function (sh, i) {
    const rid = sh.getAttributeNS(XLSX_REL_NS, "id") || sh.getAttribute("r:id");
    const path = targets[rid] || "xl/worksheets/sheet" + (i + 1) + ".xml";
    const xml = files[path];
    if (!xml) return;
    const ws = parseXml(xml, "This Excel file");
    const lines = [];
    Array.prototype.forEach.call(ws.getElementsByTagNameNS("*", "row"), function (row) {
      const cells = [];
      Array.prototype.forEach.call(row.getElementsByTagNameNS("*", "c"), function (c) {
        const t = c.getAttribute("t") || "";
        const v = childNamed(c, "v");
        const f = childNamed(c, "f");
        const is = childNamed(c, "is");
        const raw = v ? v.textContent || "" : "";
        let val = raw;
        if (t === "s") val = shared[parseInt(raw, 10)] || "";
        else if (t === "inlineStr") val = is ? xlsxStringOf(is) : "";
        else if (t === "b") val = raw === "1" ? "TRUE" : raw === "0" ? "FALSE" : raw;
        const formula = f ? String(f.textContent || "").trim() : "";
        if (val === "" && !formula) return;
        cells.push((c.getAttribute("r") || "") + "=" + val + (formula ? " {=" + formula + "}" : ""));
      });
      if (cells.length) lines.push(cells.join(" | "));
    });
    if (lines.length) out.push({ name: String(sh.getAttribute("name") || "Sheet " + (i + 1)), text: lines.join("\n") });
  });
  return out;
}

// ~200-char excerpt of a page centered on the first query word found in it.
function makeSnippet(text, query, len) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (t.length <= len) return t;
  const low = t.toLowerCase();
  const words = String(query || "").toLowerCase().split(/[^a-z0-9%$.]+/).filter(function (w) { return w.length >= 3; });
  let at = -1;
  const tryWord = function (w) {
    const i = low.indexOf(w);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  };
  words.forEach(tryWord);
  if (at === -1) words.filter(function (w) { return w.length >= 6; }).forEach(function (w) { tryWord(w.slice(0, 5)); });
  if (at === -1) return t.slice(0, len).replace(/\s+$/, "") + "…";
  let start = Math.max(0, at - Math.floor(len / 3));
  const end = Math.min(t.length, start + len);
  start = Math.max(0, end - len);
  return (start > 0 ? "…" : "") + t.slice(start, end).trim() + (end < t.length ? "…" : "");
}

// ═══════════════════════════════════════════════════════════════
// Blob / canvas / image helpers (browser)
// ═══════════════════════════════════════════════════════════════

// base64 of a Blob via FileReader — never fetch() a data: URL (prod CSP).
function blobToBase64(blob) {
  return new Promise(function (resolve, reject) {
    const r = new FileReader();
    r.onload = function () {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : "");
    };
    r.onerror = function () { reject(new Error("Couldn't read the image data from this file.")); };
    r.readAsDataURL(blob);
  });
}

function readBlobText(blob) {
  if (typeof blob.text === "function") return blob.text();
  return new Promise(function (resolve, reject) {
    const r = new FileReader();
    r.onload = function () { resolve(String(r.result || "")); };
    r.onerror = function () { reject(new Error("Couldn't read this file.")); };
    r.readAsText(blob);
  });
}

function readBlobArrayBuffer(blob) {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise(function (resolve, reject) {
    const r = new FileReader();
    r.onload = function () { resolve(r.result); };
    r.onerror = function () { reject(new Error("Couldn't read this file.")); };
    r.readAsArrayBuffer(blob);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(function (resolve, reject) {
    const fail = function () { reject(new Error("Couldn't turn the page into an image (the page may be too large).")); };
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(function (b) { if (b) resolve(b); else fail(); }, type, quality);
      return;
    }
    const b = dataUrlToBlob(canvas.toDataURL(type, quality));
    if (b) resolve(b); else fail();
  });
}

function releaseCanvas(canvas) {
  // Zeroing the size frees the backing store right away (Safari is stingy here).
  canvas.width = 0;
  canvas.height = 0;
}

// Decode an image Blob into something drawImage() accepts. EXIF rotation is
// honored on both paths in current browsers.
async function loadBitmap(blob) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(blob, { imageOrientation: "from-image" }); } catch (_e) { /* try the next way */ }
    try { return await createImageBitmap(blob); } catch (_e) { /* fall back to <img> */ }
  }
  return new Promise(function (resolve, reject) {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't open this image. It may be damaged, or in a format the browser can't read (HEIC photos: export as JPG first)."));
    };
    img.src = url;
  });
}

const OCR_TYPES = ["image/jpeg", "image/png", "image/webp"];
function ocrType(t) {
  const m = baseMime(t);
  return m === "image/jpg" ? "image/jpeg" : m;
}
function base64Len(bytes) { return Math.ceil(bytes / 3) * 4; }

// Shrink to `maxEdge` on the long side and re-encode as JPEG. Returns the
// original blob untouched when it is already small enough and a type the
// OCR endpoint takes (a crisp PNG screenshot stays a PNG).
async function downscaleImage(blob, maxEdge, quality) {
  const src = await loadBitmap(blob);
  const w0 = src.naturalWidth || src.width;
  const h0 = src.naturalHeight || src.height;
  if (!w0 || !h0) {
    if (typeof src.close === "function") src.close();
    throw new Error("This image has no pixels in it.");
  }
  if (Math.max(w0, h0) <= maxEdge && OCR_TYPES.indexOf(ocrType(blob.type)) !== -1 && base64Len(blob.size) <= MAX_OCR_BASE64) {
    if (typeof src.close === "function") src.close();
    return blob;
  }
  const s = Math.min(1, maxEdge / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * s));
  const h = Math.max(1, Math.round(h0 * s));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(src, 0, 0, w, h);
  if (typeof src.close === "function") src.close();
  try {
    return await canvasToBlob(canvas, "image/jpeg", quality);
  } finally {
    releaseCanvas(canvas);
  }
}

// → {media_type, data} for kb-ocr, re-encoding/shrinking until it fits.
async function prepareOcrImage(blob) {
  const type = ocrType(blob.type);
  if (OCR_TYPES.indexOf(type) !== -1 && base64Len(blob.size) <= MAX_OCR_BASE64) {
    return { media_type: type, data: await blobToBase64(blob) };
  }
  const tries = [[IMAGE_MAX_EDGE, 0.85], [1600, 0.8], [1300, 0.75], [1000, 0.7]];
  for (let i = 0; i < tries.length; i++) {
    const out = await downscaleImage(blob, tries[i][0], tries[i][1]);
    const t = ocrType(out.type) || "image/jpeg";
    if (OCR_TYPES.indexOf(t) !== -1 && base64Len(out.size) <= MAX_OCR_BASE64) {
      return { media_type: t, data: await blobToBase64(out) };
    }
  }
  throw new Error("This image is too large to transcribe, even after shrinking it.");
}

// ═══════════════════════════════════════════════════════════════
// pdf.js — loaded lazily (its own webpack chunk, so the rest of the CRM
// doesn't pay ~1 MB of PDF engine on every login)
// ═══════════════════════════════════════════════════════════════

let _pdfjs = null;
function loadPdfjs() {
  if (!_pdfjs) {
    _pdfjs = import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf")
      .then(function (mod) {
        const lib = mod && typeof mod.getDocument === "function" ? mod : (mod && mod.default) || mod;
        if (!lib || typeof lib.getDocument !== "function") throw new Error("pdf.js has no getDocument()");
        lib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
        return lib;
      })
      .catch(function (e) {
        _pdfjs = null;
        throw new Error("The PDF reader couldn't load (" + errText(e) + "). Reload the page and try again.");
      });
  }
  return _pdfjs;
}

// The PDF itself is the problem (not the connection) — fetching it another
// way won't help.
function isPdfContentError(e) {
  const name = (e && e.name) || "";
  return name === "PasswordException" || name === "InvalidPDFException" || name === "FormatError";
}

function pdfOpenError(e) {
  const name = (e && e.name) || "";
  if (name === "PasswordException") {
    return new Error("This PDF is password-protected. Remove the password (print it to a new PDF) and upload it again.");
  }
  if (name === "InvalidPDFException" || name === "FormatError") {
    return new Error("This file isn't a readable PDF. It may be damaged, or not really a PDF.");
  }
  return new Error("Couldn't open the PDF: " + errText(e));
}

async function openPdf(bytes) {
  const pdfjs = await loadPdfjs();
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const task = pdfjs.getDocument({ data, isEvalSupported: false, verbosity: 0 });
  try {
    return await task.promise;
  } catch (e) {
    try { task.destroy(); } catch (_e) { /* already gone */ }
    throw pdfOpenError(e);
  }
}

// Viewer only: let pdf.js pull just the byte ranges a page needs from a
// signed link, instead of downloading a 150 MB guideline to draw one cited
// page. If the storage server won't do range requests (or doesn't expose the
// headers that say so), pdf.js quietly reads the whole file from the same
// request — no worse than a download. Throws pdf.js's raw error.
async function openPdfUrl(url) {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({
    url, isEvalSupported: false, verbosity: 0,
    disableAutoFetch: true, disableStream: true, rangeChunkSize: PDF_RANGE_CHUNK,
  });
  try {
    return await task.promise;
  } catch (e) {
    try { task.destroy(); } catch (_e) { /* already gone */ }
    throw e;
  }
}

// Share of the page (0…1+) painted with pictures — enough to tell "a page of
// text with a logo" from "a page whose content IS a picture" (a scanned
// bulletin under a digital footer, a pasted screenshot of a rate matrix).
// Walks the operator list tracking the current transform: every image op
// paints the unit square, so its area on the page is |det(CTM)|.
function mulMatrix(m, t) {
  return [
    m[0] * t[0] + m[2] * t[1], m[1] * t[0] + m[3] * t[1],
    m[0] * t[2] + m[2] * t[3], m[1] * t[2] + m[3] * t[3],
    m[0] * t[4] + m[2] * t[5] + m[4], m[1] * t[4] + m[3] * t[5] + m[5],
  ];
}

async function imageCoverage(page, pdfjs) {
  const OPS = pdfjs && pdfjs.OPS;
  if (!OPS) return 0;
  const vp = page.getViewport({ scale: 1 });
  const area = Math.abs(vp.width * vp.height);
  if (!area) return 0;
  const DISABLE = pdfjs.AnnotationMode ? pdfjs.AnnotationMode.DISABLE : 0;
  const list = await page.getOperatorList({ annotationMode: DISABLE });
  const fn = list.fnArray;
  const args = list.argsArray;
  const stack = [];
  let m = [1, 0, 0, 1, 0, 0];
  let covered = 0;
  const det = function () { return Math.abs(m[0] * m[3] - m[1] * m[2]); };
  for (let i = 0; i < fn.length && covered < area; i++) {
    const op = fn[i];
    const a = args[i] || [];
    if (op === OPS.save) stack.push(m);
    else if (op === OPS.restore) { if (stack.length) m = stack.pop(); }
    else if (op === OPS.transform) { if (a.length === 6) m = mulMatrix(m, a); }
    else if (op === OPS.paintFormXObjectBegin) { stack.push(m); if (a[0] && a[0].length === 6) m = mulMatrix(m, a[0]); }
    else if (op === OPS.paintFormXObjectEnd) { if (stack.length) m = stack.pop(); }
    else if (op === OPS.paintImageXObject || op === OPS.paintInlineImageXObject || op === OPS.paintImageMaskXObject) {
      covered += det();   // 1-bit scans are usually image MASKS, so those count too
    } else if (op === OPS.paintImageXObjectRepeat) {
      covered += det() * Math.abs((a[1] || 0) * (a[2] || 0)) * ((a[3] && a[3].length) || 0) / 2;
    } else if (op === OPS.paintImageMaskXObjectRepeat) {
      covered += det() * Math.abs((a[1] || 0) * (a[4] || 0) - (a[2] || 0) * (a[3] || 0)) * ((a[5] && a[5].length) || 0) / 2;
    }
  }
  return covered / area;
}

// Render one page to an image Blob, at most maxWidth px wide (and never more
// than MAX_RENDER_PIXELS in total, so a giant poster page can't blow memory).
async function renderPdfPage(pdf, pageNo, maxWidth, type, quality) {
  const page = await pdf.getPage(pageNo);
  try {
    const base = page.getViewport({ scale: 1 });
    let scale = Math.min(maxWidth / base.width, 4);
    const pixels = base.width * base.height * scale * scale;
    if (pixels > MAX_RENDER_PIXELS) scale *= Math.sqrt(MAX_RENDER_PIXELS / pixels);
    const vp = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(vp.width));
    canvas.height = Math.max(1, Math.floor(vp.height));
    try {
      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;
      return await canvasToBlob(canvas, type, quality);
    } finally {
      releaseCanvas(canvas);
    }
  } finally {
    try { page.cleanup(); } catch (_e) { /* best effort */ }
  }
}

// Loaded PDFs for the page viewer, keyed by doc id. Small LRU: flipping pages
// of the same document never re-fetches it, and old ones get destroyed.
// Entries: {p: Promise<PDFDocumentProxy>, at, viaLink}. viaLink = streamed
// from a signed link (which expires, so those are re-opened after
// PDF_LINK_MAX_AGE). _streamPdfs flips off for the session the first time
// streaming fails where a plain download works.
const _pdfCache = new Map();
let _streamPdfs = true;
function evictPdf(id) {
  const entry = _pdfCache.get(id);
  if (!entry) return;
  _pdfCache.delete(id);
  entry.p.then(function (d) { d.destroy(); }).catch(function () { /* never loaded */ });
}
function cachedPdf(doc, forceDownload) {
  const hit = _pdfCache.get(doc.id);
  if (hit && !(hit.viaLink && Date.now() - hit.at > PDF_LINK_MAX_AGE) && !(forceDownload && hit.viaLink)) {
    _pdfCache.delete(doc.id);
    _pdfCache.set(doc.id, hit);
    return hit.p;
  }
  if (hit) evictPdf(doc.id);
  const entry = { p: null, at: Date.now(), viaLink: false };
  entry.p = (async function () {
    if (_streamPdfs && !forceDownload) {
      let url = null;
      try { url = await signFilePath(doc, PDF_LINK_SECONDS); } catch (_e) { /* the download below reports the real problem */ }
      if (url) {
        try {
          const pdf = await openPdfUrl(url);
          entry.viaLink = true;
          return pdf;
        } catch (e) {
          if (isPdfContentError(e)) throw pdfOpenError(e);
          _streamPdfs = false;   // can't stream from here — plain downloads from now on
        }
      }
    }
    const { data, error } = await requireDb().storage.from(KB_BUCKET).download(doc.file_path);
    if (error || !data) throw storageError("Loading \"" + (doc.title || "the document") + "\"", error || { message: "no data returned" });
    return openPdf(await readBlobArrayBuffer(data));
  })();
  _pdfCache.set(doc.id, entry);
  entry.p.catch(function () { if (_pdfCache.get(doc.id) === entry) _pdfCache.delete(doc.id); });
  while (_pdfCache.size > PDF_CACHE_MAX) evictPdf(_pdfCache.keys().next().value);
  return entry.p;
}

// Signed link to a document's stored file (no row lookup — `d` is a row).
async function signFilePath(d, seconds) {
  const secs = Math.max(60, Math.round(Number(seconds) || 3600));
  const { data, error } = await requireDb().storage.from(KB_BUCKET).createSignedUrl(d.file_path, secs);
  if (error || !data || !data.signedUrl) throw storageError("Opening the original file", error || { message: "no link returned" });
  return data.signedUrl;
}

// Rows we've seen, so the viewer can be handed a bare {docId} from a chat
// source or a search hit without another round-trip.
const _rows = new Map();
function remember(row) { if (row && row.id) _rows.set(row.id, row); return row; }

async function resolveDoc(doc) {
  if (!doc) throw new Error("No document was given.");
  if (typeof doc === "object" && doc.id && Object.prototype.hasOwnProperty.call(doc, "file_path")) return doc;
  const id = typeof doc === "string" ? doc : doc.id || doc.docId || doc.doc_id;
  if (!id) throw new Error("No document was given.");
  const known = _rows.get(id);
  if (known) return known;
  const { data, error } = await requireDb().from("kb_documents").select("*").eq("id", id).maybeSingle();
  if (error) throw dbError("Looking up the document", error);
  if (!data) throw new Error("That document isn't in the library any more (it may have been deleted).");
  return remember(data);
}

// ═══════════════════════════════════════════════════════════════
// Public API (contract: docs/lender-library-design.md → Client library)
// ═══════════════════════════════════════════════════════════════

export async function getAccessToken() {
  const db = requireDb();
  let session = null;
  try {
    const { data, error } = await db.auth.getSession();
    if (error) throw error;
    session = data && data.session;
  } catch (e) {
    const err = new Error("Couldn't check your sign-in (" + errText(e) + "). Reload the page and sign in again.");
    err.code = "no_session";
    throw err;
  }
  if (!session || !session.access_token) {
    const err = new Error("You're not signed in. Sign in as the admin to use the Lender Library.");
    err.code = "no_session";
    throw err;
  }
  return session.access_token;
}

// All documents: lender A→Z, newest effective date first (undated last), title.
export async function listDocuments() {
  const db = requireDb();
  const all = [];
  for (let from = 0; ; from += LIST_PAGE) {
    const { data, error } = await db.from("kb_documents").select("*")
      .order("lender", { ascending: true })
      .order("effective_date", { ascending: false, nullsFirst: false })
      .order("title", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + LIST_PAGE - 1);
    if (error) throw dbError("Loading the library", error);
    const rows = data || [];
    for (let i = 0; i < rows.length; i++) all.push(rows[i]);
    if (rows.length < LIST_PAGE) break;
  }
  _rows.clear();
  all.forEach(remember);
  return all;
}

async function insertDocRow(fields) {
  const db = requireDb();
  for (let attempt = 0; ; attempt++) {
    const row = Object.assign({}, fields, { id: newKbDocId(), superseded: false, status: "processing", page_count: 0 });
    const { data, error } = await db.from("kb_documents").insert(row).select().single();
    if (!error) return remember(data);
    if (error.code === "23505" && attempt === 0) continue;   // id collision — mint another
    throw dbError("Creating the library record", error);
  }
}

// Thrown when the document's row disappears mid-ingest (Delete pressed on a
// 'processing' row, or from another tab): the ingest stops right there
// instead of transcribing pages nobody can see, and nothing is re-created.
function docGoneError() {
  const e = new Error("This document was deleted while it was being added, so adding it stopped.");
  e.code = "doc_gone";
  return e;
}

function pageRow(docId, p) {
  return { doc_id: docId, page: p.page, text: sanitizeText(p.text || ""), ocr: !!p.ocr };
}

// upsert, so a retried batch whose first try actually landed can't trip the primary key
async function upsertPages(db, rows) {
  let lastErr = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    const { error } = await db.from("kb_pages").upsert(rows, { onConflict: "doc_id,page" });
    if (!error) return;
    if (String(error.code || "") === "23503") throw docGoneError();   // FK: the document row is gone
    lastErr = dbError("Saving the extracted pages", error);
    if (!lastErr.transient) break;
    await sleep(1500);
  }
  throw lastErr;
}

async function savePages(docId, pages, progress) {
  const db = requireDb();
  const total = pages.length;
  for (let i = 0; i < total; i += PAGE_BATCH) {
    await upsertPages(db, pages.slice(i, i + PAGE_BATCH).map(function (p) { return pageRow(docId, p); }));
    const done = Math.min(i + PAGE_BATCH, total);
    progress("save", done, total, "Saving pages: " + done + " of " + total);
  }
}

// page → {ocr, hasText} for the kb_pages rows a document already has (Retry).
async function loadSavedPages(docId) {
  const db = requireDb();
  const out = new Map();
  for (let from = 0; ; from += LIST_PAGE) {
    const { data, error } = await db.from("kb_pages").select("page,ocr,text")
      .eq("doc_id", docId).order("page", { ascending: true }).range(from, from + LIST_PAGE - 1);
    if (error) throw dbError("Reading the pages saved so far", error);
    const rows = data || [];
    rows.forEach(function (r) { out.set(r.page, { ocr: !!r.ocr, hasText: nonSpaceLen(r.text) > 0 }); });
    if (rows.length < LIST_PAGE) break;
  }
  return out;
}

// `warning` (ready documents only): which scanned pages couldn't be
// transcribed. It rides in the `error` column — a ready row with `error` set
// is searchable but has holes, and the UI offers "Retry pages".
async function markReady(id, pageCount, warning) {
  const { data, error } = await requireDb().from("kb_documents")
    .update({ page_count: pageCount, status: "ready", error: warning ? String(warning).slice(0, 1000) : null, updated_at: nowIso() })
    .eq("id", id).select();
  if (error) throw dbError("Finishing the library record", error);
  if (!data || !data.length) throw docGoneError();
  return remember(data[0]);
}

// → the updated row; null when the row is gone; undefined when unknown (the
// update itself failed — network down, session expired).
async function markError(id, message) {
  try {
    const { data, error } = await requireDb().from("kb_documents")
      .update({ status: "error", error: String(message || "Unknown error").slice(0, 1000), updated_at: nowIso() })
      .eq("id", id).select();
    if (error) return undefined;
    if (!data || !data.length) return null;
    return remember(data[0]);
  } catch (_e) { return undefined; }   // the original error is the one worth showing
}

// onProgress gets {stage, page, pages, message, docId}; docId is null until
// the kb_documents row exists (the UI uses it to know which row is busy).
function progressFn(onProgress) {
  let docId = null;
  const fn = function (stage, page, pages, message) {
    emit(onProgress, { stage, page: page || 0, pages: pages || 0, message: message || "", docId });
  };
  fn.setDoc = function (id) { docId = id || null; };
  return fn;
}

// Touches a running ingest's row (updated_at) once a minute for as long as
// it runs — through a long upload too — so the Library tab can tell a live
// ingest from one whose tab closed mid-way; and notices when the row has been
// deleted under it. check() throws docGoneError once that's been seen; the
// pipeline calls it before each page. A failed touch is ignored (a blip
// shouldn't sink the ingest — the next page save will tell).
function startHeartbeat(docId) {
  let gone = false;
  let busy = false;
  const timer = setInterval(function () {
    if (busy || gone) return;
    busy = true;
    Promise.resolve()
      .then(function () { return requireDb().from("kb_documents").update({ updated_at: nowIso() }).eq("id", docId).select("id"); })
      .then(function (res) { if (res && !res.error && (!res.data || !res.data.length)) gone = true; }, function () { /* ignore */ })
      .then(function () { busy = false; });
  }, HEARTBEAT_MS);
  return {
    check: function () { if (gone) throw docGoneError(); },
    stop: function () { clearInterval(timer); },
  };
}

function ocrHint(doc, page) {
  return (doc.lender + " " + doc.title + " p." + page).replace(/\s+/g, " ").trim().slice(0, 200);
}

// kb-ocr with retries for the transient failures (rate limit, 5xx, network).
async function ocrWithRetry(blob, hint) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await ocrImageBlob(blob, hint);
    } catch (e) {
      if (!e || !e.transient || attempt >= OCR_RETRY_DELAYS.length) throw e;
      await sleep(OCR_RETRY_DELAYS[attempt]);
    }
  }
}

// Failures that would hit every page the same way (signed out, endpoint not
// deployed, no API key, row deleted) stop the ingest. Anything else is that
// page's own problem — Claude declined it, it timed out, it's unreadable —
// so it's skipped and the rest carry on.
function isFatalOcrError(e) {
  if (!e) return false;
  const code = String(e.code || "");
  if (e.name === "AbortError" || code === "doc_gone" || code === "no_session") return true;
  if (code === "anthropic_key_not_configured" || code === "invalid_model" || code === "messages_required" || code === "FUNCTION_PAYLOAD_TOO_LARGE") return true;
  const s = Number(e.status) || 0;
  return s === 401 || s === 403 || s === 404 || s === 413;
}

// Run `task` over `items` with at most `limit` in flight. Waits for every
// started task to settle before rethrowing the first failure, so nothing is
// still rendering when the caller tears the PDF down.
async function runPool(items, limit, task) {
  let next = 0;
  let firstErr = null;
  async function worker() {
    while (firstErr === null && next < items.length) {
      const item = items[next++];
      try { await task(item); } catch (e) { if (firstErr === null) firstErr = e || new Error("Unknown error"); }
    }
  }
  const workers = [];
  const n = Math.max(1, Math.min(limit, items.length));
  for (let i = 0; i < n; i++) workers.push(worker());
  await Promise.all(workers);
  if (firstErr) throw firstErr;
}

// A transcription normally holds everything the text layer had (the model
// reads the whole rendered page), so it replaces it — unless it came back
// shorter, then both are kept so nothing is lost.
function mergeOcrText(layer, ocr) {
  const a = String(layer || "").trim();
  const b = String(ocr || "").trim();
  if (!b) return a;
  if (!a || nonSpaceLen(b) >= nonSpaceLen(a)) return b;
  return a + "\n\n" + b;
}

// Transcribe `todo` (page numbers) via kb-ocr, OCR_CONCURRENCY at a time,
// saving each page the moment it's done — a later failure or a closed tab
// never throws away pages already paid for, and Retry skips them (ocr:true,
// even when the page turned out blank). Returns [{page, error}] for pages
// that couldn't be transcribed. Throws only on a failure that would hit every
// page (isFatalOcrError) or OCR_FAIL_STREAK failures in a row.
async function ocrPages(docId, todo, pages, pageImage, hint, progress, hb, kind) {
  const failed = [];
  const total = todo.length;
  if (!total) return failed;
  const db = requireDb();
  const label = function (n) { return kind === "image" ? "the image" : "scanned page " + n; };
  let finished = 0;
  let streak = 0;
  progress("ocr", 0, total, kind === "image" ? "Transcribing the image…" : plural(total, "scanned page") + " to transcribe…");
  await runPool(todo, OCR_CONCURRENCY, async function (pageNo) {
    hb.check();
    let text;
    try {
      const img = await pageImage(pageNo);
      text = await ocrWithRetry(img, hint(pageNo));
    } catch (e) {
      if (isFatalOcrError(e)) {
        if (e.code === "doc_gone" || e.name === "AbortError") throw e;
        const err = new Error("Couldn't transcribe " + label(pageNo) + ": " + errText(e));
        err.code = e.code;
        err.status = e.status;
        throw err;
      }
      failed.push({ page: pageNo, error: e });
      finished++;
      streak++;
      if (streak >= OCR_FAIL_STREAK) {
        const err = new Error(plural(streak, "scanned page") + " in a row couldn't be transcribed, so this stopped (last: page " +
          pageNo + ": " + errText(e) + "). Pages finished so far are saved — Retry picks up from there.");
        err.code = e && e.code;
        throw err;
      }
      progress("ocr", finished, total, "Couldn't transcribe page " + pageNo + " — skipping it (" + finished + " of " + total + ")");
      return;
    }
    streak = 0;
    const row = { page: pageNo, text: mergeOcrText(pages[pageNo - 1] && pages[pageNo - 1].text, text), ocr: true };
    pages[pageNo - 1] = row;
    await upsertPages(db, [pageRow(docId, row)]);
    finished++;
    progress("ocr", finished, total, kind === "image" ? "Image transcribed." :
      "Transcribing scanned pages: " + finished + " of " + total + " (page " + pageNo + ")");
  });
  return failed;
}

function ocrWarning(failed) {
  const nums = failed.map(function (f) { return f.page; }).sort(function (a, b) { return a - b; });
  const one = nums.length === 1;
  return plural(nums.length, "scanned page") + " couldn't be transcribed (p. " + nums.slice(0, 15).join(", ") +
    (nums.length > 15 ? "…" : "") + "), so " + (one ? "it isn't" : "they aren't") + " searchable yet. Use Retry pages to try " +
    (one ? "it" : "them") + " again. First problem: " + errText(failed[0].error);
}

// pdf.js text layer for every page, plus which pages need transcribing: no
// usable text layer (a scan), or little text with pictures covering a good
// part of the page (a scan under a digital footer, a pasted screenshot of a
// table) — that picture content would otherwise never be searchable.
async function pdfTextPass(pdf, progress, hb) {
  const pdfjs = await loadPdfjs();
  const n = pdf.numPages;
  const pages = new Array(n);
  const scans = [];
  for (let i = 1; i <= n; i++) {
    progress("extract", i, n, "Reading text: page " + i + " of " + n);
    hb.check();
    let text = "";
    let scan = false;
    try {
      const pg = await pdf.getPage(i);
      try {
        text = pdfItemsToText((await pg.getTextContent()).items);
        const len = nonSpaceLen(text);
        if (len < MIN_TEXT_CHARS) scan = true;
        else if (len < SPARSE_TEXT_CHARS) {
          let cover = 0;
          try { cover = await imageCoverage(pg, pdfjs); } catch (_e) { cover = 0; }
          scan = cover >= IMAGE_COVER_MIN;
        }
      } finally { pg.cleanup(); }
    } catch (_e) {
      text = "";   // a broken text layer on one page → treat it like a scan
      scan = true;
    }
    pages[i - 1] = { page: i, text, ocr: false };
    if (scan) scans.push(i);
  }
  return { pages, scans };
}

async function extractTextFilePages(blob, name, progress) {
  progress("extract", 0, 0, "Reading the text…");
  const isCsv = extOf(name) === "csv" || baseMime(blob.type) === "text/csv";
  const parts = splitTextPages(await readBlobText(blob), TEXT_PAGE_CHARS, { repeatHeader: isCsv });
  progress("extract", parts.length, parts.length, "Split into " + plural(parts.length, "page") + ".");
  return parts.map(function (t, i) { return { page: i + 1, text: t, ocr: false }; });
}

// .docx → ~3,000-char pages, like a pasted note. .xlsx → a page per sheet
// (a long sheet is split further, its "Sheet: name" line repeated on each).
async function extractOfficePages(blob, kind, progress) {
  progress("extract", 0, 0, kind === "xlsx" ? "Reading the spreadsheet…" : "Reading the Word document…");
  const buf = await readBlobArrayBuffer(blob);
  let parts = [];
  if (kind === "docx") {
    const files = await unzipText(buf, function (n) { return n === "word/document.xml"; });
    if (!files["word/document.xml"]) throw new Error("This doesn't look like a Word (.docx) file — there's no document inside it.");
    parts = splitTextPages(docxText(files["word/document.xml"]), TEXT_PAGE_CHARS);
  } else {
    const files = await unzipText(buf, function (n) {
      return n === "xl/workbook.xml" || n === "xl/_rels/workbook.xml.rels" || n === "xl/sharedStrings.xml" || /^xl\/worksheets\/[^/]+\.xml$/.test(n);
    });
    if (!files["xl/workbook.xml"]) throw new Error("This doesn't look like an Excel (.xlsx) file — there's no workbook inside it.");
    xlsxSheets(files).forEach(function (s) {
      const text = "Sheet: " + s.name + "\n" + s.text;
      if (text.length <= TEXT_PAGE_CHARS * 2) parts.push(text);
      else parts = parts.concat(splitTextPages(text, TEXT_PAGE_CHARS, { repeatHeader: true }));
    });
  }
  progress("extract", parts.length, parts.length, "Split into " + plural(parts.length, "page") + ".");
  return parts.map(function (t, i) { return { page: i + 1, text: t, ocr: false }; });
}

// Office documents are re-read from scratch every time (nothing to pay for
// twice), so a re-read replaces the pages in place and then drops any left
// over past the new end.
async function dropPagesAfter(docId, lastPage) {
  const { error } = await requireDb().from("kb_pages").delete().eq("doc_id", docId).gt("page", lastPage);
  if (error) throw dbError("Removing the old extra pages", error);
}

const NO_TEXT = "No readable text was found in this file. If it's a scan or photo, try a sharper copy.";

// Everything after the upload: extract → save → transcribe → ready. `saved`
// (Retry only) is loadSavedPages(): rows already stored aren't re-written and
// their transcriptions aren't paid for twice. Pages that still couldn't be
// transcribed don't sink the document — it goes 'ready' with a warning that
// names them (see markReady).
async function processDoc(doc, blob, kind, progress, saved, hb) {
  const done = saved || new Map();
  const hint = function (n) { return ocrHint(doc, n); };
  let pdf = null;
  try {
    let pages;
    let scans = [];
    let pageImage = null;
    let replace = false;
    if (kind === "docx" || kind === "xlsx") {
      pages = await extractOfficePages(blob, kind, progress);
      replace = true;
    } else if (kind === "pdf") {
      progress("extract", 0, 0, "Opening the PDF…");
      pdf = await openPdf(await readBlobArrayBuffer(blob));
      const pass = await pdfTextPass(pdf, progress, hb);
      pages = pass.pages;
      scans = pass.scans;
      pageImage = function (n) { return renderPdfPage(pdf, n, OCR_RENDER_WIDTH, "image/jpeg", OCR_JPEG_QUALITY); };
    } else if (kind === "image") {
      pages = [{ page: 1, text: "", ocr: false }];
      scans = [1];
      pageImage = function () { return downscaleImage(blob, IMAGE_MAX_EDGE, 0.9); };
    } else {
      pages = await extractTextFilePages(blob, doc.file_name || blob.name, progress);
    }
    if (!pages.length) throw new Error(NO_TEXT);

    // Every page gets its row now (a scan with whatever thin text layer it
    // has); transcriptions overwrite theirs as they land.
    hb.check();
    if (replace) {
      await savePages(doc.id, pages, progress);
      await dropPagesAfter(doc.id, pages.length);
    } else {
      await savePages(doc.id, pages.filter(function (p) { return !done.has(p.page); }), progress);
    }
    const todo = scans.filter(function (n) { const s = done.get(n); return !(s && s.ocr); });
    const failed = await ocrPages(doc.id, todo, pages, pageImage, hint, progress, hb, kind);

    // Real content = a page with a proper text layer, or one transcribed
    // (now or on an earlier run). A scan's stray text layer ("Page 3 of 10")
    // doesn't count — all-scan + every transcription failed is a failure.
    const scanSet = new Set(scans);
    const anyText = pages.some(function (p) { return nonSpaceLen(p.text) > 0 && (p.ocr || !scanSet.has(p.page)); }) ||
      Array.from(done.values()).some(function (s) { return s.ocr && s.hasText; });
    if (!anyText) {
      if (!failed.length) throw new Error(NO_TEXT);
      const f = failed[0];
      const err = new Error("Couldn't transcribe " + (kind === "image" ? "the image" : "scanned page " + f.page) + ": " + errText(f.error));
      err.code = f.error && f.error.code;
      throw err;
    }
    const ready = await markReady(doc.id, pages.length, failed.length ? ocrWarning(failed) : null);
    progress("done", pages.length, pages.length, "Ready: " + plural(pages.length, "page") + " indexed" +
      (failed.length ? " — " + plural(failed.length, "page") + " couldn't be transcribed." : "."));
    return ready;
  } finally {
    if (pdf) { try { pdf.destroy(); } catch (_e) { /* best effort */ } }
  }
}

async function uploadOriginal(doc, file, mime, progress, overwrite) {
  const db = requireDb();
  progress("upload", 0, 0, "Uploading " + (file.name || "file") + " (" + fmtBytes(file.size) + ")…");
  const path = doc.id + "/" + safeFileName(file.name || doc.file_name);
  const up = await db.storage.from(KB_BUCKET).upload(path, file, { contentType: mime, upsert: !!overwrite, cacheControl: "3600" });
  if (up.error) throw storageError("Uploading the file", up.error);
  const setPath = await db.from("kb_documents").update({ file_path: path, updated_at: nowIso() }).eq("id", doc.id).select("id");
  if (setPath.error) throw dbError("Recording the uploaded file", setPath.error);
  if (!setPath.data || !setPath.data.length) {
    // Deleted while the upload ran: its folder sweep has already happened,
    // so this object would sit in the bucket forever — take it back out.
    try { await db.storage.from(KB_BUCKET).remove([path]); } catch (_e) { /* best effort */ }
    throw docGoneError();
  }
  return remember(Object.assign({}, doc, { file_path: path }));
}

async function downloadOriginal(d, progress) {
  progress("upload", 0, 0, "Fetching the stored copy" + (d.size_bytes ? " (" + fmtBytes(d.size_bytes) + ")" : "") + "…");
  const { data, error } = await requireDb().storage.from(KB_BUCKET).download(d.file_path);
  if (error || !data) throw storageError("Fetching the stored copy of \"" + (d.title || "the document") + "\"", error || { message: "no data returned" });
  return data;
}

// Common failure path. The row is left at status 'error' with the message
// (so the UI can show it and offer Retry/Delete) and rides on err.doc —
// except when the row is gone (deleted mid-way: nothing to show, err.code
// 'doc_gone'), or with `keepRow` (a Retry on a document that is already
// 'ready': it stays ready and searchable, exactly as it was).
async function ingestFailure(e, doc, progress, keepRow) {
  const msg = errText(e);
  const err = e instanceof Error ? e : new Error(msg);
  let gone = !!(e && e.code === "doc_gone");
  if (doc && !gone && !keepRow) {
    const row = await markError(doc.id, msg);
    if (row === null) gone = true;
    else err.doc = Object.assign({}, row || _rows.get(doc.id) || doc, { status: "error", error: msg });
  }
  if (gone && doc) {
    _rows.delete(doc.id);
    evictPdf(doc.id);
    err.code = "doc_gone";
  }
  progress("error", 0, 0, msg);
  return err;
}

// Upload → extract → index one file. See the contract for the stages.
// On failure the kb_documents row is left with status 'error' + the message
// (Retry → retryIngest picks up from there), onProgress gets {stage:'error'},
// and the thrown Error carries err.doc when a row exists.
export async function ingestFile(file, meta, onProgress) {
  const progress = progressFn(onProgress);
  let doc = null;
  let hb = null;
  try {
    requireDb();
    if (!file || typeof file.size !== "number") throw new Error("No file was selected.");
    const kind = fileKind(file);
    if (!kind) {
      throw new Error("Unsupported file type: \"" + (file.name || "this file") + "\". Upload a PDF, an image (PNG, JPG, WebP), a Word (.docx) or Excel (.xlsx) file, or a text file (.txt, .md, .csv).");
    }
    if (!file.size) throw new Error("\"" + file.name + "\" is empty (0 bytes).");
    const m = normalizeMeta(meta, titleFromFileName(file.name));
    const mime = mimeFor(file, kind);

    doc = await insertDocRow(Object.assign({}, m, { file_name: file.name || null, mime, size_bytes: file.size }));
    progress.setDoc(doc.id);
    hb = startHeartbeat(doc.id);
    doc = await uploadOriginal(doc, file, mime, progress, false);
    return await processDoc(doc, file, kind, progress, null, hb);
  } catch (e) {
    throw await ingestFailure(e, doc, progress, false);
  } finally {
    if (hb) hb.stop();
  }
}

// Pick up a document that didn't finish, keeping its record and every page
// already saved; only what's missing runs again (pages never transcribed go
// back to Claude — nothing is paid for twice). Used for Retry on a failed
// upload, "Retry pages" on a ready document with untranscribed pages, and a
// row left 'processing' when its tab closed. `file`: the original File if
// the browser still has it (skips a download; required when the upload never
// finished), else null and the stored copy is used. A document that is
// already 'ready' stays ready and searchable throughout — and unchanged if
// the retry fails. Throws err.code 'doc_gone' when the record no longer exists.
export async function retryIngest(doc, file, onProgress) {
  const progress = progressFn(onProgress);
  let d = null;
  let wasReady = false;
  let hb = null;
  try {
    const db = requireDb();
    const id = typeof doc === "string" ? doc : doc && (doc.id || doc.docId || doc.doc_id);
    if (!id) throw new Error("No document was given to retry.");
    const got = await db.from("kb_documents").select("*").eq("id", id).maybeSingle();
    if (got.error) throw dbError("Looking up the document", got.error);
    if (!got.data) throw docGoneError();
    d = remember(got.data);
    progress.setDoc(d.id);
    wasReady = d.status === "ready";
    const local = file && typeof file.size === "number" && file.size ? file : null;
    if (!d.file_path && !local) {
      throw new Error("There's no stored file to pick up from (it never finished uploading, or this was pasted text). Delete this entry and add it again.");
    }
    // Word / Excel originals are read as such — never as text (that would
    // index the ZIP bytes). The old binary .doc / .xls can't be read here.
    const office = officeKind(d);
    if (office === "legacy") {
      throw new Error("\"" + (d.title || d.file_name || "This file") + "\" is an old-format Word/Excel file (.doc / .xls), which the library can't re-read. " +
        "Save it as .docx / .xlsx (or PDF) and add that instead" + (wasReady ? " — its current pages stay searchable until then." : "."));
    }
    const kind = office || (local && fileKind(local)) || docKind(d);
    if (!kind) throw new Error("Unsupported file type: \"" + (d.file_name || d.title || "this file") + "\".");
    if (!wasReady) {
      const st = await db.from("kb_documents").update({ status: "processing", error: null, updated_at: nowIso() }).eq("id", d.id).select("id");
      if (st.error) throw dbError("Restarting the document", st.error);
      if (!st.data || !st.data.length) throw docGoneError();
      d = remember(Object.assign({}, d, { status: "processing", error: null }));
    }
    hb = startHeartbeat(d.id);
    let src = local;
    if (!d.file_path) d = await uploadOriginal(d, local, mimeFor(local, kind), progress, true);
    else if (!src) src = await downloadOriginal(d, progress);
    progress("extract", 0, 0, "Checking which pages are already saved…");
    const saved = await loadSavedPages(d.id);
    return await processDoc(d, src, kind, progress, saved, hb);
  } catch (e) {
    throw await ingestFailure(e, d, progress, wasReady);
  } finally {
    if (hb) hb.stop();
  }
}

// Pasted text (an AE email, a scenario desk answer…) → a document with no
// file, split into ~3,000-char pages. Optional onProgress like ingestFile.
export async function addTextNote(meta, text, onProgress) {
  const progress = progressFn(onProgress);
  let doc = null;
  try {
    requireDb();
    const body = sanitizeText(text).trim();
    if (!body) throw new Error("Paste some text first.");
    const firstLine = (body.split("\n").find(function (l) { return l.trim(); }) || "").trim();
    const m = normalizeMeta(meta, firstLine.slice(0, 80) || "Pasted note");
    const parts = splitTextPages(body, TEXT_PAGE_CHARS);
    const pages = parts.map(function (t, i) { return { page: i + 1, text: t, ocr: false }; });

    doc = await insertDocRow(Object.assign({}, m, {
      file_path: null, file_name: null, mime: "text/plain", size_bytes: new Blob([body]).size,
    }));
    progress.setDoc(doc.id);
    await savePages(doc.id, pages, progress);
    const ready = await markReady(doc.id, pages.length, null);
    progress("done", pages.length, pages.length, "Saved: " + plural(pages.length, "page") + ".");
    return ready;
  } catch (e) {
    throw await ingestFailure(e, doc, progress, false);
  }
}

// Edit the descriptive fields only. Returns the updated row.
export async function updateDocument(id, patch) {
  const db = requireDb();
  const docId = typeof id === "object" && id ? id.id : id;
  if (!docId) throw new Error("No document was given to update.");
  const p = patch || {};
  const has = function (k) { return Object.prototype.hasOwnProperty.call(p, k); };
  const row = { updated_at: nowIso() };
  if (has("title")) {
    const t = String(p.title == null ? "" : p.title).trim();
    if (!t) throw new Error("The title can't be blank.");
    row.title = t.slice(0, 300);
  }
  if (has("lender")) row.lender = canonical(KB_LENDERS, p.lender, "Other", "lender");
  if (has("doc_type")) row.doc_type = canonical(KB_DOC_TYPES, p.doc_type, "Guideline", "document type");
  if (has("programs")) row.programs = normalizePrograms(p.programs);
  if (has("effective_date")) row.effective_date = normalizeDate(p.effective_date);
  if (has("superseded")) row.superseded = !!p.superseded;
  if (has("notes")) row.notes = String(p.notes == null ? "" : p.notes).trim();
  const { data, error } = await db.from("kb_documents").update(row).eq("id", docId).select().single();
  if (error) throw dbError("Saving the document details", error);
  return remember(data);
}

// Remove the stored file(s), then the row (kb_pages cascade).
export async function deleteDocument(doc) {
  const db = requireDb();
  const id = typeof doc === "string" ? doc : doc && (doc.id || doc.docId || doc.doc_id);
  if (!id) throw new Error("No document was given to delete.");
  const paths = new Set();
  // Only ever inside this document's own `${id}/` folder — a stale or
  // mismatched row object must not be able to take another document's file.
  if (doc && typeof doc === "object" && doc.file_path && String(doc.file_path).indexOf(id + "/") === 0) paths.add(doc.file_path);
  // Also sweep the doc's own folder: catches a file whose upload finished
  // but whose path never got written back (an ingest that died mid-way).
  try {
    const listed = await db.storage.from(KB_BUCKET).list(id, { limit: 1000 });
    (listed.data || []).forEach(function (o) { if (o && o.name) paths.add(id + "/" + o.name); });
  } catch (_e) { /* listing is best effort; file_path above still gets removed */ }
  if (paths.size) {
    const { error } = await db.storage.from(KB_BUCKET).remove(Array.from(paths));
    if (error && !/not found/i.test(error.message || "")) {
      throw storageError("Deleting the stored file (nothing was deleted)", error);
    }
  }
  const { data, error } = await db.from("kb_documents").delete().eq("id", id).select("id");
  if (error) throw dbError("Deleting the library record", error);
  if (!data || !data.length) {
    throw new Error("The library record wasn't deleted (it may already be gone, or this login lacks permission). Reload the library.");
  }
  _rows.delete(id);
  evictPdf(id);
  return true;
}

// ═══════════════════════════════════════════════════════════════
// Rate sheets — which older sheet a new one replaces, and the hand-off from
// Settings → "Upload Rate Sheet PDF" (the sheet that prices the calculator
// also lands here, so "is HomeSafe available in Utah?" or "what's the 1.75
// margin paying?" can be answered off the current sheet). Superseded sheets
// drop out of answers (kb_search skips them), so retiring the old one is
// what keeps last week's pricing and state lists from being quoted.
// ═══════════════════════════════════════════════════════════════

export const KB_RATE_SHEET = "Rate sheet";
// Fired on window after a sheet is added from outside the Library screen, so
// a mounted Library re-reads its list instead of waiting for its refresh.
export const KB_CHANGED_EVENT = "lf-kb-changed";

// The rate-sheet extractor's lender codes ("FOA", "MoO", "Mutual",
// "Longbridge", "SmartFi", "OtherLender" — src/rateSheetExtractor.js) or a
// spelled-out name → a KB_LENDERS name. Anything unrecognized is "Other".
export function kbLenderFromExtraction(raw) {
  const s = String(raw == null ? "" : raw).trim().toLowerCase();
  if (!s) return "Other";
  const exact = KB_LENDERS.find(function (l) { return l.toLowerCase() === s; });
  if (exact) return exact;
  const flat = s.replace(/[^a-z]/g, "");
  if (flat === "foa" || flat === "far" || flat.indexOf("financeofamerica") >= 0 || flat.indexOf("fareverse") >= 0) return "Finance of America";
  if (flat === "moo" || flat === "mom" || flat === "mutual" || flat.indexOf("mutualofomaha") >= 0 || flat.indexOf("omahamortgage") >= 0) return "Mutual of Omaha";
  if (flat.indexOf("smartfi") >= 0) return "SmartFi";
  if (flat.indexOf("longbridge") >= 0 || flat === "lbf") return "Longbridge";
  return "Other";
}

// normalizeDate that never throws (a date it can't read is no date) and also
// takes a timestamp's leading YYYY-MM-DD.
function isoDay(v) {
  const s = typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v) ? v.slice(0, 10) : v;
  try { return normalizeDate(s); } catch (_e) { return null; }
}
// "2026-09-29" → "9/29/26"
function shortDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? (+m[2]) + "/" + (+m[3]) + "/" + m[1].slice(2) : "";
}
function sameLender(a, b) { return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase(); }

// Current (not superseded) "Rate sheet" documents from `lender` dated before
// (cmp < 0) or after (cmp > 0) `effectiveDate`, newest first. An undated
// sheet can't be shown to be older or newer, so it's left alone; nothing is
// returned for "Other" (it lumps unrelated lenders together — one of them
// can't retire another's sheet) or without a readable date.
function currentSheetsDated(docs, lender, effectiveDate, excludeId, cmp) {
  const day = isoDay(effectiveDate);
  const l = String(lender || "").trim();
  if (!day || !l || sameLender(l, "Other")) return [];
  return (Array.isArray(docs) ? docs : []).filter(function (d) {
    if (!d || d.superseded || d.doc_type !== KB_RATE_SHEET || !sameLender(d.lender, l)) return false;
    if (excludeId && d.id === excludeId) return false;
    const dd = isoDay(d.effective_date);
    return !!dd && (cmp < 0 ? dd < day : dd > day);
  }).sort(function (a, b) {
    const x = isoDay(a.effective_date), y = isoDay(b.effective_date);
    return x === y ? 0 : (x < y ? 1 : -1);
  });
}

// The sheets a new one effective `effectiveDate` replaces: only the "Rate
// sheet" type, only the same lender, only strictly older effective dates,
// only ones not already superseded, never `excludeId` (the new one itself).
export function olderRateSheets(docs, lender, effectiveDate, excludeId) {
  return currentSheetsDated(docs, lender, effectiveDate, excludeId, -1);
}
// Same rules, the other way: current sheets from that lender dated AFTER it.
export function newerRateSheets(docs, lender, effectiveDate, excludeId) {
  return currentSheetsDated(docs, lender, effectiveDate, excludeId, 1);
}

// The library already holds this sheet: the same file (name + size), or a
// rate sheet from the same lender with the same effective date. A copy that
// failed to process (status 'error') doesn't count. → the row, or null.
export function findSameRateSheet(docs, info) {
  const i = info || {};
  const day = isoDay(i.effectiveDate);
  const name = String(i.fileName || "").trim().toLowerCase();
  const size = Number(i.size) || 0;
  const list = (Array.isArray(docs) ? docs : []).filter(function (d) { return d && d.status !== "error"; });
  const sameFile = name && size ? list.find(function (d) {
    return String(d.file_name || "").trim().toLowerCase() === name && Number(d.size_bytes) === size;
  }) : null;
  if (sameFile) return sameFile;
  if (!day || !i.lender || sameLender(i.lender, "Other")) return null;
  return list.find(function (d) {
    return d.doc_type === KB_RATE_SHEET && sameLender(d.lender, i.lender) && isoDay(d.effective_date) === day;
  }) || null;
}

// Settings → Apply on an extracted rate sheet hands the PDF here. It goes in
// as a "Rate sheet" (unless that file, or that lender's sheet for that date,
// is already here), then that lender's older current sheets are marked
// superseded. If the library already has a NEWER sheet from the lender, the
// one just added is marked superseded instead, so it can't compete with it.
//   info: {lender (extractor code or name), effectiveDate, programs?, title?, notes?}
//   api:  stand-ins for listDocuments / ingestFile / updateDocument (tests)
// Resolves {added, duplicate, doc, lender, effectiveDate, superseded: [rows],
// supersededSelf, newer, supersedeError}. Throws only when the add itself
// fails — a failed supersede is reported in supersedeError instead.
export async function addRateSheetToLibrary(file, info, onProgress, api) {
  const a = Object.assign({ listDocuments: listDocuments, ingestFile: ingestFile, updateDocument: updateDocument }, api || {});
  const i = info || {};
  if (!file || typeof file.size !== "number") throw new Error("There's no rate sheet file to add.");
  const lender = kbLenderFromExtraction(i.lender);
  const day = isoDay(i.effectiveDate);
  const docs = (await a.listDocuments()) || [];
  const dup = findSameRateSheet(docs, { lender: lender, effectiveDate: day, fileName: file.name, size: file.size });
  const res = { added: false, duplicate: !!dup, doc: dup || null, lender: lender, effectiveDate: day, superseded: [], supersededSelf: false, newer: null, supersedeError: "" };
  if (!dup) {
    const raw = String(i.lender == null ? "" : i.lender).trim();
    const who = lender !== "Other" ? lender : (raw && !/^other/i.test(raw) ? raw : "");
    const title = String(i.title == null ? "" : i.title).trim() ||
      [who, "rate sheet", shortDay(day) || titleFromFileName(file.name)].filter(Boolean).join(" ");
    const notes = i.notes != null ? i.notes : "Added from Settings when this sheet was applied to the calculator's pricing.";
    res.doc = await a.ingestFile(file, { title: title, lender: lender, doc_type: KB_RATE_SHEET, effective_date: day, programs: i.programs || [], notes: notes }, onProgress);
    res.added = true;
  }
  const doc = res.doc;
  if (doc && doc.id) {
    // Dated by the library's own row (a same-file match may carry its own date).
    const others = docs.filter(function (d) { return d && d.id !== doc.id; });
    const older = olderRateSheets(others, doc.lender, doc.effective_date, doc.id);
    for (let k = 0; k < older.length; k++) {
      try { res.superseded.push((await a.updateDocument(older[k].id, { superseded: true })) || older[k]); }
      catch (e) { if (!res.supersedeError) res.supersedeError = "couldn't mark “" + (older[k].title || "an older sheet") + "” superseded (" + errText(e) + ")"; }
    }
    if (res.added) {
      const newer = newerRateSheets(others, doc.lender, doc.effective_date, doc.id);
      if (newer.length) {
        res.newer = newer[0];
        try { res.doc = (await a.updateDocument(doc.id, { superseded: true })) || Object.assign({}, doc, { superseded: true }); res.supersededSelf = true; }
        catch (e) {
          const nd = shortDay(isoDay(newer[0].effective_date));
          const msg = "the library has a newer " + (nd ? nd + " " : "") + "sheet, but marking this one superseded failed (" + errText(e) + ")";
          res.supersedeError = res.supersedeError ? res.supersedeError + "; " + msg : msg;
        }
      }
    }
  }
  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent(KB_CHANGED_EVENT));
    }
  } catch (_e) { /* a stale Library list refreshes on its own */ }
  return res;
}

// One status line for what addRateSheetToLibrary did → {tone: "ok"|"warn", text}.
export function rateSheetLibraryNote(res) {
  const r = res || {};
  const n = (r.superseded || []).length;
  const parts = [];
  if (r.duplicate) parts.push("Already in the Lender Library" + (r.doc && r.doc.title ? " as “" + r.doc.title + "”" : "") + " — not added again");
  else if (r.supersededSelf) {
    const nd = shortDay(isoDay(r.newer && r.newer.effective_date));
    parts.push("Also added to the Lender Library, marked superseded — it already has the newer " + (nd ? nd + " " : "") + "sheet");
  } else parts.push("Also added to the Lender Library");
  if (n) parts.push(n === 1 ? "older sheet marked superseded" : n + " older sheets marked superseded");
  if (r.supersedeError) parts.push(r.supersedeError);
  return { tone: r.supersedeError ? "warn" : "ok", text: parts.join(" · ") };
}

// Full-text search across every ready page. Rows are kb_search's columns plus
// `snippet` (~200 chars around the first hit).
export async function searchPages(query, opts) {
  const q = String(query || "").trim();
  if (!q) return [];
  const o = opts || {};
  const lenders = Array.isArray(o.lenders) && o.lenders.length ? o.lenders : null;
  const { data, error } = await requireDb().rpc("kb_search", {
    queries: [q.slice(0, 500)],
    lenders,
    include_superseded: !!o.includeSuperseded,
    max_results: Math.max(1, Math.min(200, Math.round(Number(o.limit) || 30))),
  });
  if (error) throw dbError("Searching the library", error);
  return (data || []).map(function (r) { return Object.assign({}, r, { snippet: makeSnippet(r.text, q, 200) }); });
}

function cleanHistory(history) {
  if (!Array.isArray(history)) return [];
  const out = [];
  history.forEach(function (h) {
    if (!h) return;
    let role = null;
    if (h.role === "user") role = "user";
    else if (h.role === "assistant") role = "assistant";
    let text = "";
    if (typeof h.text === "string") text = h.text;
    else if (typeof h.content === "string") text = h.content;
    text = text.trim();
    if (!role || !text) return;
    out.push({ role, text: text.length > MAX_HISTORY_TEXT ? text.slice(0, MAX_HISTORY_TEXT) : text });
  });
  return out.slice(-MAX_HISTORY);
}

function cleanFilters(filters) {
  if (!filters || typeof filters !== "object") return null;
  const out = {};
  if (Array.isArray(filters.lenders)) {
    const l = filters.lenders.filter(function (x) { return typeof x === "string" && x.trim(); });
    if (l.length) out.lenders = l;
  }
  if (filters.includeSuperseded) out.includeSuperseded = true;
  return Object.keys(out).length ? out : null;
}

// Deal basics a question may carry — the chat's removable "Using: TX · 72 ·
// $650K · HECM refi" chip (src/libraryChat.js). Mirrors the server's check
// (cleanContext in api/_lib/kb.js): known keys only, in a fixed order;
// numbers only, bounded and rounded; text only from fixed vocabularies.
// Never a name, phone, email, street address or id — there is no key for
// any of them.
export const DEAL_STATE_CODES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","PR","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
// "second" = FOA's HomeSafe Second: a 2nd lien behind a forward first
// mortgage that stays in place (the balance sent is that first lien).
const DEAL_TYPES = ["refi", "first", "purchase", "second"];
// The words a deal's text fields may use — nothing free-form ever reaches a
// prompt. loanType and stage must be one of these exactly (any case); a
// pitched product's name (Calc writes "HECM ARM 1.750% margin", "SecureEquity
// Fixed @ 7.990%", "Choice Fixed Max @ 8.250%"…) must be built only from
// these words, rates and margins. The server keeps the same lists
// (api/_lib/kb.js) — tests/api/kb.test.js checks them against each other and
// DEAL_STAGES against src/constants.js.
export const DEAL_LOAN_TYPES = ["HECM", "HECM for Purchase", "Proprietary", "Jumbo", "FHA", "HomeSafe", "HomeSafe Second", "SecureEquity", "Choice", "Platinum", "Equity Preserve", "HELOC for Seniors"];
export const DEAL_STAGES = ["Prospect", "Pitched", "Scheduled Counseling", "Docs Out", "Appraisal Ordered", "Underwriting", "Approved", "Clear to Close", "Closed", "Funded", "Fallout — Qualified", "Fallout — Denied", "Fallout — No App"];
export const DEAL_PRODUCT_WORDS = ["finance", "of", "america", "foa", "far", "longbridge", "lb", "smartfi", "mutual", "omaha", "moo", "hecm", "h4p", "arm", "annual", "monthly", "cmt", "sofr", "fixed", "plus", "select", "standard", "std", "intro", "second", "2nd", "homesafe", "secureequity", "secure", "equity", "choice", "flex", "service", "max", "pro", "core", "saver", "lite", "platinum", "preserve", "peak", "loc", "line", "credit", "tier", "a", "b", "c", "heloc", "seniors", "for", "purchase", "jumbo", "proprietary", "margin", "rate", "reverse", "mortgage"];
const DEAL_PRODUCT_TOKEN = /^(@|[-–—|·]|\d{1,2}(\.\d{1,3})?%?)$/;
const DEAL_PRODUCT_MAX = 80;
function dealPick(v, list) {
  if (typeof v !== "string") return "";
  const t = v.replace(/\s+/g, " ").trim().toLowerCase();
  for (let i = 0; i < list.length; i++) if (list[i].toLowerCase() === t) return list[i];
  return "";
}
function dealProduct(v) {
  if (typeof v !== "string") return "";
  const t = v.replace(/\s+/g, " ").trim();
  if (!t || t.length > DEAL_PRODUCT_MAX) return "";
  return t.split(" ").every(function (w) { return DEAL_PRODUCT_WORDS.indexOf(w.toLowerCase()) >= 0 || DEAL_PRODUCT_TOKEN.test(w); }) ? t : "";
}
function dealNum(v, lo, hi) {
  return typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi ? Math.round(v) : null;
}
export function cleanDealContext(c) {
  if (!c || typeof c !== "object" || Array.isArray(c)) return null;
  const out = {};
  const st = typeof c.state === "string" ? c.state.trim().toUpperCase() : "";
  if (DEAL_STATE_CODES.indexOf(st) >= 0) out.state = st;
  const age = dealNum(c.age, 18, 120);
  if (age != null) out.age = age;
  const hv = dealNum(c.homeValue, 1000, 100000000);
  if (hv != null) out.homeValue = hv;
  const mb = dealNum(c.mortgageBalance, 0, 100000000);
  if (mb != null) out.mortgageBalance = mb;
  const lt = dealPick(c.loanType, DEAL_LOAN_TYPES);
  if (lt) out.loanType = lt;
  if (DEAL_TYPES.indexOf(c.dealType) >= 0) out.dealType = c.dealType;
  else if (typeof c.isRefi === "boolean") out.dealType = c.isRefi ? "refi" : "first";
  const pr = dealProduct(c.product);
  if (pr) out.product = pr;
  const sg = dealPick(c.stage, DEAL_STAGES);
  if (sg) out.stage = sg;
  return Object.keys(out).length ? out : null;
}

const STREAM_ERROR_DEFAULTS = {
  bad_request: "The question couldn't be sent (the server called it a bad request).",
  no_library: "Your library is empty. Upload lender documents in the Library tab first.",
  refusal: "Claude declined to answer this question.",
  upstream_error: "The AI service had a problem answering. Try again in a minute.",
  db_error: "Couldn't search the library database. Try again; if it keeps happening, check that " + SQL_FILE + " has been run.",
};

function streamEventError(evt) {
  const code = String(evt.code || "error");
  const e = new Error(evt.message ? String(evt.message) : (STREAM_ERROR_DEFAULTS[code] || "Something went wrong while answering."));
  e.code = code;
  return e;
}

// Ask the chatbot. params: {question, history, filters, wantSources, premium, context, signal}
// — wantSources:true when the question asks where the answer comes from (the
// answer then names its documents and pages in the text); premium:true is
// "Dig deeper" (the stronger model reading twice the pages — ≈ 15–20¢ a
// question instead of ≈ 4–7¢); context: the deal basics on screen
// (cleanDealContext — sent only while its chip is shown). The `final` event carries
// cost: {usd, model, premium} (+ partial:true when the answer was cut off,
// so the figure is a floor). onEvent(evt) gets
// every NDJSON object as it arrives (status / queries / delta / final /
// error). Resolves with the `final` event; rejects on an `error` event, a
// non-200, a dropped stream, or Stop (signal) — the Stop rejection has
// err.name === "AbortError".
export async function askLibrary(params, onEvent) {
  const p = params || {};
  const signal = p.signal || null;
  const q = String(p.question == null ? "" : p.question).trim();
  if (!q) throw new Error("Type a question first.");
  if (q.length > MAX_QUESTION) {
    throw new Error("That question is too long (" + q.length.toLocaleString() + " characters; the limit is 4,000).");
  }
  if (signal && signal.aborted) throw abortError();

  const body = { action: "kb-ask", question: q, history: cleanHistory(p.history) };
  const filters = cleanFilters(p.filters);
  if (filters) body.filters = filters;
  if (p.wantSources === true) body.wantSources = true;
  if (p.premium === true) body.premium = true;
  const context = cleanDealContext(p.context);
  if (context) body.context = context;
  const token = await getAccessToken();
  if (signal && signal.aborted) throw abortError();

  const what = "Asking the library";
  let finalEvt = null;
  let errEvt = null;
  const junk = [];
  const parser = createNdjsonParser(function (obj, raw) {
    if (finalEvt || errEvt) return;
    if (!obj || typeof obj.type !== "string") {
      if (junk.length < 3) junk.push(String(raw).slice(0, 200));
      return;
    }
    if (obj.type === "error") errEvt = obj;
    else if (obj.type === "final") finalEvt = obj;
    emit(onEvent, obj);
  });

  const link = linkedController(signal, STREAM_IDLE_MS);
  try {
    let resp;
    try {
      resp = await fetch(PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify(body),
        signal: link.signal,
      });
    } catch (_e) {
      throw fetchFailure(link, signal, what, false);
    }
    link.bump();
    if (!resp.ok) throw await httpError(resp, what);

    if (resp.body && typeof resp.body.getReader === "function") {
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      try {
        for (;;) {
          let chunk;
          try { chunk = await reader.read(); } catch (_e) { throw fetchFailure(link, signal, what, true); }
          if (chunk.done) break;
          link.bump();
          parser.push(decoder.decode(chunk.value, { stream: true }));
          if (finalEvt || errEvt) break;
        }
        if (!finalEvt && !errEvt) {
          parser.push(decoder.decode());
          parser.end();
        }
      } finally {
        try { reader.cancel().catch(function () { /* already closed */ }); } catch (_e) { /* ignore */ }
      }
    } else {
      // No streaming body (very old browser): read it all, then parse.
      let txt;
      try { txt = await resp.text(); } catch (_e) { throw fetchFailure(link, signal, what, true); }
      parser.push(txt);
      parser.end();
    }
  } finally {
    link.done();
  }

  if (errEvt) throw streamEventError(errEvt);
  if (finalEvt) return finalEvt;
  if (signal && signal.aborted) throw abortError();
  if (link.state.timedOut) throw fetchFailure(link, signal, what, true);
  if (junk.length) throw new Error(what + " failed: the server sent an unexpected response: " + junk[0]);
  throw new Error(what + " failed: the answer was cut off before it finished (the server may have timed out). Try again, or ask a narrower question.");
}

// Time-limited link to the original file (for "Open original at this page").
export async function getSignedUrl(doc, seconds = 3600) {
  const d = await resolveDoc(doc);
  if (!d.file_path) {
    throw new Error("\"" + (d.title || "This document") + "\" was pasted in as text, so there's no original file to open.");
  }
  return signFilePath(d, seconds);
}

// One page's stored text — exactly what search and the chatbot read. The
// viewer shows it for pasted notes and .txt/.md/.csv documents (which have
// no page image) and under "Show this page's text" for the rest. "" when the
// page is blank; throws when there's no such page.
export async function getPageText(doc, page) {
  const id = typeof doc === "string" ? doc : doc && (doc.id || doc.docId || doc.doc_id);
  if (!id) throw new Error("No document was given.");
  const n = Math.round(Number(page) || 1);
  const { data, error } = await requireDb().from("kb_pages").select("text").eq("doc_id", id).eq("page", n).maybeSingle();
  if (error) throw dbError("Loading the text of page " + n, error);
  if (!data) {
    const known = _rows.get(id) || (typeof doc === "object" ? doc : null);
    const title = (known && known.title) || "this document";
    throw new Error("Page " + n + " of \"" + title + "\" has no saved text" +
      (known && known.status && known.status !== "ready" ? " yet (it hasn't finished processing)." : "."));
  }
  return String(data.text || "");
}

// Picture of one page for the viewer.
//   PDF   → rendered PNG, returned as a blob: object URL (caller should
//           URL.revokeObjectURL it when the viewer moves on)
//   image → the signed URL of the original
//   text  → null (pasted notes and .txt/.md/.csv have no page image)
// `doc` may be a kb_documents row, a chat source ({docId}), a search hit
// ({doc_id}), or an id string.
export async function renderPageImage(doc, page, { maxWidth = 1100 } = {}) {
  const d = await resolveDoc(doc);
  const kind = docKind(d);
  if (!d.file_path || kind === "text" || !kind) return null;
  if (kind === "image") return getSignedUrl(d);
  let pdf = await cachedPdf(d);
  const n = Math.round(Number(page) || 1);
  if (n < 1 || n > pdf.numPages) {
    throw new Error("Page " + n + " doesn't exist: \"" + (d.title || "this document") + "\" has " + plural(pdf.numPages, "page") + ".");
  }
  const width = Math.max(200, Math.min(4000, Number(maxWidth) || 1100));
  let blob;
  try {
    blob = await renderPdfPage(pdf, n, width, "image/png");
  } catch (e) {
    const entry = _pdfCache.get(d.id);
    if (!entry || !entry.viaLink) throw e;
    // The streamed copy failed partway (a range request refused or expired):
    // draw this page from a full download instead, and if that works, stop
    // streaming for the rest of the session.
    pdf = await cachedPdf(d, true);
    blob = await renderPdfPage(pdf, n, width, "image/png");
    _streamPdfs = false;
  }
  return URL.createObjectURL(blob);
}

// Transcribe one image (JPEG/PNG/WebP Blob) via kb-ocr → plain text.
// Oversized images are shrunk to fit the request cap first.
export async function ocrImageBlob(blob, hint) {
  if (!blob || typeof blob.size !== "number" || !blob.size) throw new Error("There's no image to transcribe.");
  const image = await prepareOcrImage(blob);
  const body = { action: "kb-ocr", image };
  const h = String(hint == null ? "" : hint).replace(/\s+/g, " ").trim().slice(0, 200);
  if (h) body.hint = h;
  const token = await getAccessToken();
  const what = "Transcribing the page";

  const link = linkedController(null, OCR_TIMEOUT_MS);
  let resp;
  let raw = "";
  try {
    try {
      resp = await fetch(PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify(body),
        signal: link.signal,
      });
    } catch (_e) {
      throw fetchFailure(link, null, what, false);
    }
    if (!resp.ok) throw await httpError(resp, what);
    try { raw = await resp.text(); } catch (_e) { throw fetchFailure(link, null, what, true); }
  } finally {
    link.done();
  }
  let data = null;
  try { data = JSON.parse(raw); } catch (_e) { data = null; }
  if (!data || typeof data.text !== "string") {
    throw new Error(what + " failed: the server sent back an unreadable response.");
  }
  return sanitizeText(data.text).trim();
}

// Shared with the expense importer's receipt reader (src/receiptReader.js,
// 10/9) so the app has ONE pdf.js loader / page renderer / image shrinker.
export { loadPdfjs, openPdf, renderPdfPage, prepareOcrImage, blobToBase64, readBlobArrayBuffer };
